import { Capacitor } from "@capacitor/core";
import type { AxiosRequestConfig, AxiosResponse } from "axios";
import type { DateTime } from "luxon";
import queryString from "query-string";
import type { AppleAuthResponse } from "react-apple-signin-auth";
import type { MonthlySortEntries, ShiftCellDisplay, ShiftTimeSeparation } from "../components/staffing/ScheduleExport/types";
import type { BulkNurseWelcomeResponse, ResendWelcomePreflightResponse } from "../components/team/ResendWelcome/types";
import type { OpenStatusDecision, StatusDecisionChoice } from "../components/team/statusDecisions/types";
import type { Department, DepartmentJobShiftTimes, Token, UserInfo } from "../contexts/AuthProvider";
import type {
	CareersApplicationCreateInput,
	CareersApplicationDetail,
	CareersApplicationDetailsPatch,
	CareersApplicationEndInput,
	CareersApplicationsResponse,
	CareersCandidateMatchResponse,
	CareersCommitInput,
	CareersCvSource,
	CareersCvTextResponse,
	CareersCvUpload,
	CareersCvUploadStatus,
	CareersCvUploadsResponse,
	CareersFeedbackInput,
	CareersJob,
	CareersJobInput,
	CareersJobStatus,
	CareersJobsResponse,
	CareersMentionableUsersResponse,
	CareersNoteInput,
} from "../pages/Careers/types";
import type { AiPrintoutSettings } from "../pages/DaySchedule/printoutAiSettings";
import type { PresetSettings, PrintoutPresetsResponse } from "../pages/DaySchedule/printoutPresets";
import type { NurseShiftLineageEntry } from "../pages/EmployeeChangelog/shiftLineage";
import type {
	HiringConversationResponse,
	HiringFacilitiesResponse,
	HiringFacilityOption,
	HiringMessage,
	HiringTemplatesResponse,
	HiringThreadsResponse,
	SyncStatusResponse,
} from "../pages/HiringChat/types";
import { buildHubCandidateParams } from "../pages/HiringHub/hubParams";
import type {
	CreateCandidateRequest,
	CreatedInterviewResponse,
	DocumentUrlResponse,
	HubAccess,
	HubCandidateFilters,
	HubCandidateProfile,
	HubCandidatesResponse,
	HubDepartmentUsersResponse,
	HubDocType,
	HubEditableDepartmentsResponse,
	HubInterviewerAssignmentsResponse,
	HubInterviewersResponse,
	HubProcessNote,
	HubScheduleResponse,
	HubTabAccess,
	InterviewerCandidate,
	ProcessNoteRequest,
	UpdateCandidateRequest,
	UpdateInterviewRequest,
	UpdatePipelineStatusRequest,
	UpdateProcessRequest,
} from "../pages/HiringHub/types";
import type {
	HiringAbsenceSettings,
	HiringAbsenceSettingsUpdate,
	HiringEligibleStaffResponse,
	HiringRecurringGapsResponse,
	HiringStaffedNursesResponse,
} from "../pages/HiringModule/HiringPrediction/types";
import type { DepartmentAlertsResponse } from "../pages/ManagementCenter/DepartmentAlerts/types";
import type { MultiFacilityGridResponse } from "../pages/ManagementCenter/MultiFacility/multiFacilityGridColumns";
import type { DifferenceMode } from "../pages/ManagementCenter/ScheduleVsActual/types";
import type { CoverageSummaryResponse, CoverageTargetMode, PeopleRotationResponse } from "../pages/MasterSchedule/v3/types";
import type { HeldCellValue } from "../pages/OnboardingTools/hooks";
import type { AddToDepartmentItem, AddToDepartmentResponse, CreateHeldResponse } from "../pages/OnboardingTools/sections/heldRowModel";
import type { ConflictDecision, ResolveConflictsResult } from "../pages/OnboardingTools/sections/LoadResultDisplay";
import type {
	DataLoadValidationSummariesResponse,
	DepartmentInputUpdate,
	DepartmentUsersResponse,
	IncentiveShiftsResponse,
	NotificationOptOutsResponse,
	OpsBackofficeResponse,
	OpsPreset,
	OpsPresetCreatePayload,
	OpsPresetsResponse,
	OpsPresetUpdatePayload,
	SupportUsersResponse,
	UpdateOpsBackofficeUserInputResponse,
} from "../pages/OpsBackoffice/types";
import type {
	AirtableFacilitiesResponse,
	AirtablePreviewResponse,
	AirtableVerifyResponse,
} from "../pages/Settings/DepartmentConfiguration/forms/airtable/airtableImportTypes";
import type {
	ClipboardEnrollmentResponse,
	ClipboardRoleMappingApplyBody,
	ClipboardRoleMappingProposalResponse,
	ClipboardVerifyCredentialsResponse,
} from "../pages/Settings/DepartmentConfiguration/forms/clipboard/clipboardOnboardingTypes";
import type {
	AdminRole,
	AdminRoleDefaults,
	AdminUser,
	AdminUserPermissions,
	FlagAdminMetadata,
	GeneralFormData,
	UpdateAdminUsersResponse,
} from "../pages/Settings/DepartmentConfiguration/forms/types";
import type { WageRulesConfigResponse, WageSettingsResponse } from "../types/wageRules";
import { AnnieAvatarSaveKind } from "../utils/annieAvatarSaveKind";
import type { AnnieAvatarSaveRequest, AnnieAvatarSettings, AnnieFeedbackRequest, AnnieFeedbackResponse } from "../utils/annieStream";
import type { TrainingOrientationWageBody } from "../utils/DepartmentWage.config";
import { convertIsoDates } from "../utils/dateUtils";
import type {
	DepartmentSilentJobsResponse,
	JobNotificationChangesRequest,
	MyJobNotificationsResponse,
	NurseJobNotificationsResponse,
	SetMyJobNotificationRequest,
} from "../utils/jobNotificationTypes";
import {
	type Agency,
	type AgencyDeactivationImpact,
	type AgencyIntegrationToggleResult,
	type AgencyPostingFailure,
	type AgencySource,
	type AgencyWageBulkUpsertRequest,
	type AgencyWageBulkUpsertResponse,
	type AgentReportView,
	type AgentSource,
	type BatchCostPredictionRequest,
	type BatchCostPredictionResponse,
	type BreakRulesConfig,
	type BulkNurseJobsApplyRequest,
	type BulkNurseJobsPreview,
	type BulkNurseJobsRequest,
	type BulkNurseJobsResult,
	type CalledOffShift,
	type CalledOffShiftResponse,
	type CallInReason,
	type CallOffReplacementGateResponse,
	type CancelCallOffs,
	type CensusGrouping,
	type CensusPointInTimes,
	type CensusPointInTimesParams,
	type CertificationJobMapping,
	type CertMappingDeleteResult,
	type CertPrimaryJobChangePreview,
	ChangesSource,
	type ChatBotRequest,
	type ChatbotMessage,
	ClientType,
	type CombinedMessagesResponse,
	type CostDeepDiveResponse,
	type CostDeepDiveUnit,
	type CostPredictionRequest,
	type CostPredictionResponse,
	type CostVarianceResponse,
	type CreateBatchUsersForNurseResponse,
	type CreateLocationCredentialRequest,
	type CreateRecurringShiftRequest,
	type CreateSpecificPreferenceOptions,
	type CreateTimeOffRequestBody,
	type DailyCostBreakdownResponse,
	type DailyStaffBreakdownResponse,
	type DayExpensesResponse,
	type DeactivationImpactResponse,
	type DeleteDepartmentLocationsBody,
	type DeleteDepartmentLocationsResult,
	type DeleteRecurringShiftRequest,
	type DepartmentActionsMetadataResponse,
	type DepartmentDataResponse,
	type DepartmentHasShifts,
	type DepartmentJobPpdOverride,
	type DepartmentLocation,
	type DepartmentWageDataResponse,
	type DisciplinaryReportResponse,
	type DisciplinaryRulesResponse,
	type DisciplinaryViolationType,
	type DocumentData,
	type DraftDayStatsRequest,
	type DraftDayStatsResponse,
	type EmployeeStatusMapping,
	type EmployeeStatusMappingDeleteResult,
	type EmploymentMappingDeleteResult,
	type EmploymentTypeMapping,
	type ExistingAdminUser,
	type ExternalEmployeeDiff,
	type ExternalShiftDiff,
	type ExternalSource,
	type ExternalTimeOffDiff,
	type FindDuplicateLocationsBody,
	type FindDuplicateLocationsResult,
	type FlatNurseAction,
	type GetLimitedCallOffs,
	type GroupedActivityLogChangeResult,
	type GroupedActivityLogClientTimestamps,
	type HoursRateConfig,
	type HoursSummaryResponse,
	type HrisEmployeeShiftStats,
	type HrisIgnoreCategory,
	type HrisIgnoredValueRow,
	type HrisIntegrationType,
	type HrisPtoTypeStats,
	type HrisRawEmployeeRow,
	type HubSpotCredentialUploadResponse,
	type IncentiveConfiguration,
	type IncentiveConfigurationWrite,
	type IntegrationEmployeesSummary,
	type IntegrationShiftsSummary,
	type IntegrationTimeOffSummary,
	type Job,
	type JobDepartmentSubRole,
	type JobNotEnabledEmployee,
	type LeakageReportResponse,
	type LocationCredentialResponse,
	type ManagementCenterHoursResponse,
	type ManagementCenterHPPDResponse,
	type ManagementCenterMultiDepartmentResponse,
	type ManagementCenterPPDResponse,
	type ManagementCenterStaffMixResponse,
	type ManagementCenterStringToDatesAndValueAggregation,
	type MissingRecurringPatternsSummary,
	type MultiFacilityCostStatsResponse,
	type MultiFacilityDisciplinarySummaryResponse,
	type MultiFacilityKpiSummaryResponse,
	type MultiFacilityPerformanceOverTimeResponse,
	type MultiFacilityStatsResponse,
	type NursaFacilitiesResponse,
	type NursaFacilitySearchResponse,
	type NursaReadiness,
	type NursaWebhookSubscriptionResult,
	type Nurse,
	type NurseCostsResponse,
	type NurseData,
	type NurseInShift,
	type NurseLocationHistoryResponse,
	type NurseOvertimeMap,
	type NurseProfileToSubmit,
	type NurseRole,
	type NurseScheduleResponse,
	type NurseSortItem,
	type NursesToRemoveParams,
	type OtMode,
	type OvertimeConfig,
	type PasswordResetRequestBody,
	type PBJFacilityInfo,
	type PbjAuditEmployeeRow,
	type PbjAuditTurnoverResponse,
	type PbjAuditWorkedSource,
	type PbjBreakRule,
	type PbjBreakRulesSaveBody,
	type PbjBreakRulesSaveResult,
	type PbjBreaksRemovedRow,
	type PbjCalculatorResponse,
	type PbjDailyHoursCapReportResponse,
	type PbjEmployeeOverrideIdBody,
	type PbjExcludedEmployeeRow,
	type PbjExcludeEmployeeBody,
	type PbjExclusionImportPreview,
	type PbjHoursByRoleDaily,
	type PbjJob,
	type PbjJobMappingRow,
	type PbjJobsSaveBody,
	type PbjJobsSaveResult,
	type PbjLongShiftReviewResponse,
	type PbjLongShiftRow,
	type PbjManualImportPreview,
	type PbjMissingPunchRow,
	type PbjModel,
	type PbjModelUpsertBody,
	type PbjMultipleRoleRow,
	type PbjOverrideImportPreview,
	type PbjPayTypeOverrideBody,
	type PbjPlanProgress,
	type PbjPrediction,
	type PbjPredictionJobCoverageGap,
	type PbjPredictionManualHoursCreateBody,
	type PbjPredictionManualHoursEntry,
	type PbjPredictionManualHoursUpdateBody,
	type PbjRoleSummaryRow,
	type PbjShiftImportResult,
	type PbjShiftManualEditBody,
	type PbjShortShiftReviewResponse,
	type PbjStaffingBulkEditBody,
	type PbjStaffingBulkEditResult,
	type PbjStaffingColumnVisibility,
	type PbjStaffingHoursRow,
	type PbjStaffingHoursSummary,
	type PbjStaffingShiftRow,
	type PbjSubmissionProcessType,
	type PbjSuggestedPlan,
	type PbjSuggestedPlanRequest,
	type PbjTerminatedEmployeeRow,
	type PbjTrackedPlan,
	type PbjTrackedPlanSaveBody,
	type PbjUpcomingTurnoverRow,
	type PickupApprovalItem,
	type PostChatMessageResult,
	type PostShiftRequestBody,
	type PostShiftRequestParams,
	type PrintoutOrientation,
	type PtoTrimmedSegmentsResponse,
	type PublishRequestBody,
	type PushReportColumn,
	type PushReportSchedule,
	type PushReportType,
	type RecalculateCensusResponse,
	type Recommendation,
	type RecommendationParams,
	type RecurringOvertimeMap,
	type RecurringShift,
	type RecurringShiftRecurrenceType,
	type RenameDepartmentLocationBody,
	type RenameDepartmentLocationResult,
	type Request,
	type RequestApprovalNursesParams,
	type ResultOfRecurringCreation,
	type RoleJobRecommendation,
	type SetLocationEmrBody,
	type SetLocationEmrResult,
	type SettableEmployeeStatus,
	type ShiftPunchEvent,
	type StaffCommunicationHistoryResponse,
	type StaffCommunicationSummaryResponse,
	type StatsMode,
	type SubRoleUpsertBody,
	type SyncScheduledResponse,
	type TeamNurse,
	type TeamStatsResponse,
	type TimeOffImpactResponse,
	type TimeOffMappingDeleteResult,
	type TimeOffRemapPreview,
	type TimeOffRequest,
	type TimeOffTypeMapping,
	type UpdateCensusData,
	type UpdateMultipleRecurringShiftRequest,
	type UpdateMultipleRecurringShiftRequestResult,
	type UpdateRecurringShiftRequest,
	type UpdateSingleDepartmentLocationBody,
	type UpdateSingleDepartmentLocationResult,
	type UpdateSpecificPreferenceResult,
	type UpdateUserSettingsRequest,
	type UserSentMessage,
	type UserSettings,
	type UserView,
	type WageBreakdownResult,
} from "../utils/models";
import axiosInstance from "../utils/networkWrapper";
import type {
	DismissSuspectedDuplicateRequest,
	MergeCandidatePage,
	NurseMergePreviewWire,
	NurseMergeRequest,
	NurseMergeResult,
	SuspectedDuplicatePage,
} from "../utils/nurseMergeModels";
import { localStorageService } from "./LocalStorageService";

export type WeekDayCell = {
	week_idx: number;
	day_idx: number;
};

export type ScheduleTemplateNurseCell = {
	nurse_name: string;
	cells: WeekDayCell[];
};

export type ScheduleTemplateSlot = {
	location_display: string;
	shift_start: string; // HH:mm
	shift_end: string; // HH:mm
	amount: number;
	is_outside_config: boolean;
	configured_cells: WeekDayCell[];
	nurse_cells: ScheduleTemplateNurseCell[];
};

export type ScheduleTemplateSection = {
	section_name: string;
	slots: ScheduleTemplateSlot[];
};

export type ScheduleTemplateExportPayload = {
	department_id: string;
	master_schedule_weeks: number;
	first_sunday: string; // YYYY-MM-DD
	sections: ScheduleTemplateSection[];
};

// Shared by getLatestPromptHistory / useLatestPromptHistory.
export type PromptHistoryFileType = "shifts" | "template" | "nurses";

/**
 * What a recurring-template load is about to do, fetched before the file is uploaded.
 * `live_creation_start` is the first day the load actually schedules new shifts for an employee
 * whose schedule is loaded through `loaded_shifts_end`; dates are ISO (yyyy-MM-dd).
 */
export type RecurringStartPreview = {
	loaded_shifts_start: string | null;
	loaded_shifts_end: string | null;
	live_creation_start: string | null;
	employees_in_department: number;
	employees_with_loaded_shifts: number;
	employees_without_loaded_shifts: number;
	employees_with_partial_coverage: number;
};

/* ────────────────────────────────────────────────────────────────────────────
 * "Calculate recurring" (DAT-774)
 *
 * Mirrors `backend/models/scheduling/recurring_calculation.py`. Those models carry camelCase
 * aliases and FastAPI serializes responses by alias, so — unlike `RecurringStartPreview` above —
 * these are camelCase on the wire.
 * ──────────────────────────────────────────────────────────────────────────── */

/** What the operator asks for before a run. `auto` lets the fit pick, and is the default. */
export type RequestedPattern = "4-2" | "everyWeek" | "twoWeekly" | "fourWeekly" | "custom" | "auto";

/** The shape of a fitted pattern. `rotation` drifts across the calendar; the rest name weekdays in a cycle. */
export type PatternKind = "rotation" | "everyWeek" | "twoWeekly" | "fourWeekly" | "custom";

/** Which history a fit was built from. */
export type ShiftSource = "schedule" | "punches" | "both";

export type EmployeeBucket = "ready" | "needsReview" | "skipped";

/** Everything the run refuses to decide silently — each one is shown against its row. */
export type RecurringFlag =
	| "betterPatternFound"
	| "singleSource"
	| "sourcesNotComparable"
	| "scheduleUnreliable"
	| "patternChanged"
	| "limitedData"
	| "ptoUnavailable";

/** Why an employee got no pattern at all. */
export type SkipReason = "noStandingPattern" | "notEnoughData" | "noData" | "noPrimaryJob" | "notInDepartment";

/** Where a job's official hours came from — `confirmed` means the operator sent them back. */
export type ShiftHoursSource = "confirmed" | "configured" | "departmentDefault" | "inferred";

/**
 * A `yyyy-MM-dd` field as it ACTUALLY arrives.
 *
 * `axiosInstance` runs `convertIsoDates` over every response, so any string matching the ISO date
 * shape is rehydrated into a Luxon `DateTime` before a caller ever sees it. Typing these as `string`
 * is a lie that TypeScript cannot catch — and rendering one directly throws React error #31
 * ("objects are not valid as a React child"), which is exactly what took the review screen down on
 * the preview. The union keeps the type honest for callers that bypass the interceptor (a raw
 * `fetch`, a test fixture), which is why `formatIsoDate` accepts both.
 */
export type WireDate = string | DateTime;

/** One of a job's official shift times. Times are "HH:mm:ss" — below the ISO-date regex, so NOT converted. */
export type ShiftHoursSlot = { start: string; end: string };

export type JobShiftHours = {
	jobName: string;
	slots: ShiftHoursSlot[];
	source: ShiftHoursSource;
	/** The department already has a shift-times row for this job (possibly empty), so saving would not write it. */
	hasDepartmentRow: boolean;
};

/** Hours sent back to confirm a run. Department-row presence is a fact the server reports, never one a client sends. */
export type ConfirmedShiftHours = Omit<JobShiftHours, "hasDepartmentRow">;

/** Why a job the operator ticked was not written to Department Configuration. */
export type ShiftHoursSaveSkipReason = "alreadyConfigured" | "jobNotEnabled" | "unknownJob";

export type SaveRecurringShiftHoursRequest = {
	jobs: Array<{ jobName: string; slots: ShiftHoursSlot[] }>;
};

export type SaveRecurringShiftHoursResult = {
	saved: Array<{ jobName: string; jobId: string; fullShiftTimes: [start: number, end: number][]; partialBlockShifts: number[] }>;
	skipped: Array<{ jobName: string; reason: ShiftHoursSaveSkipReason }>;
	/** The department's whole per-job table after the save, for refreshing the in-memory department. */
	jobShiftTimes: DepartmentJobShiftTimes[];
};

/**
 * Everything the operator decides before a run.
 *
 * `minimumScore` has no default on the backend and no floor — it decides how many wrong shifts land
 * on a real schedule, so the caller must state it rather than inherit one.
 */
export type RecurringCalculationSettings = {
	minimumScore: number;
	pattern: RequestedPattern;
	/** Job name → pattern, for "not the same pattern for every job". A job left out falls back to `pattern`. */
	patternByJob: Record<string, RequestedPattern>;
	baseWindowMonths: number;
	/**
	 * Opt-in retry window. `null` (the default) means the run reads exactly `baseWindowMonths` and
	 * reports what it scores there, rather than quietly answering from a longer window than the one
	 * that was asked for. When set it must exceed `baseWindowMonths`.
	 */
	extendedWindowMonths: number | null;
	/** Only meaningful with `pattern: "custom"` — the cycle length in whole weeks the operator named. */
	customCycleWeeks: number | null;
	/** The operator's confirmed hours per job, echoed back from a previous run with any corrections. */
	shiftHours: ConfirmedShiftHours[];
};

/** One line of the results table. */
export type RecurringCandidateRow = {
	candidateId: string;
	nurseId: number;
	nurseName: string;
	role: string | null;
	jobId: string | null;
	jobName: string | null;
	bucket: EmployeeBucket;
	skipReason: SkipReason | null;
	patternLabel: string | null;
	patternKind: PatternKind | null;
	workDays: number | null;
	restDays: number | null;
	weekdays: string[];
	/** `[cycleWeekIndex, weekday]` pairs for a multi-week pattern. */
	cycleSlots: Array<[number, string]>;
	anchor: WireDate | null;
	/** Weekday the cycle counts from (0 = Monday) — needed to reproduce the pattern's days client-side. */
	weekStartDay: number | null;
	/** Whole weeks in the cycle. Named kinds imply it; a custom cycle only carries it here. */
	cycleWeeks: number | null;
	shiftTimes: ShiftHoursSlot[];
	score: number | null;
	basedOn: ShiftSource | null;
	/** Schedule/punch agreement as a share, so the reviewer sees how much the two sources differ. */
	sourceMatch: number | null;
	workedDayCount: number;
	location: string | null;
	locationInferred: boolean;
	flags: RecurringFlag[];
	runnerUpLabel: string | null;
	runnerUpScore: number | null;
};

export type RecurringCalculationHeadline = {
	employees: number;
	ready: number;
	needsReview: number;
	skipped: number;
	/** Mean score across everyone who got one, measured on weeks the fit never saw. */
	accuracy: number | null;
	holdoutDays: number | null;
	windowStart: WireDate | null;
	windowEnd: WireDate | null;
	basedOn: string | null;
};

/** The whole proposal. Persists nothing until `approveRecurringCandidates` is called. */
export type RecurringCalculationResult = {
	settings: RecurringCalculationSettings;
	/** The hours the run resolved for itself, and where each came from — the operator never typed these. */
	shiftHours: JobShiftHours[];
	headline: RecurringCalculationHeadline;
	ready: RecurringCandidateRow[];
	needsReview: RecurringCandidateRow[];
	skipped: RecurringCandidateRow[];
};

/** One staffing date of an employee's real history, both sources side by side. Times are "HH:mm:ss". */
export type EmployeeHistoryDay = { day: WireDate; scheduled: ShiftHoursSlot[]; actual: ShiftHoursSlot[] };

/** An employee's loaded schedule and punches over the window, for the review calendar. */
export type RecurringEmployeeHistory = {
	nurseId: number;
	windowStart: WireDate;
	windowEnd: WireDate;
	days: EmployeeHistoryDay[];
};

/** One template the insert refused, and the raw reason it gave. */
export type FailedRecurringShift = { nurseId: number; detail: string };

/**
 * One selected employee's complete approval outcome.
 *
 * `patternCount` is the number of templates in their selected set, so the review UI can account for
 * every employee's whole schedule rather than conflating employees with individual template rows.
 */
export type RecurringSelectedOutcome =
	| {
			candidateId: string;
			nurseId: number;
			outcome: "created";
			patternCount: number;
			retiredPatternCount: number;
			reason: null;
	  }
	| {
			candidateId: string;
			nurseId: number;
			outcome: "alreadyCurrent";
			patternCount: number;
			retiredPatternCount: number;
			reason: null;
	  }
	| {
			candidateId: string;
			nurseId: number;
			outcome: "failed";
			patternCount: number;
			retiredPatternCount: number;
			reason: string;
	  };

export type RecurringCreationResult = {
	approvedCount: number;
	createdPatternCount: number;
	alreadyExistingPatternCount: number;
	createdEmployeeCount: number;
	/** Templates the approved employees already had, withdrawn from the creation date on so the new ones could take over. */
	retiredPatternCount: number;
	/** One terminal result for every selected employee/template set. */
	selectedOutcomes: RecurringSelectedOutcome[];
	failedShifts: FailedRecurringShift[];
	/** Roles whose staffing-grid location could not be generated — the templates exist, the role needs mapping. */
	unmappedRoles: Array<{ role: string; reason: string }>;
};

// How deep Claude reasons over the uploaded files. Mirrors the backend AutoPromptEffort enum, cheapest first.
export type AutoPromptEffort = "low" | "medium" | "high" | "xhigh" | "max";

// Auto Prompt analysis response. Each section is editable text the onboarder reviews before generating the final prompt.
export type AutoPromptReport = {
	schedule_symbols: string;
	locations: string;
	shift_times: string;
	name_matching: string;
	file_dates: string;
	master_schedule_orientation: string | null;
	flags: string[];
};

export type OnboardingJobType = "auto_prompt_analysis" | "csv_transform_batch";
export type OnboardingJobOrigin = "auto_prompt" | "data_load";
export type OnboardingJobStatus = "queued" | "running" | "succeeded" | "partially_succeeded" | "failed" | "cancel_requested" | "cancelled";
export type OnboardingJobStage =
	| "queued"
	| "reading_files"
	| "analyzing_files"
	| "checking_analysis"
	| "reading_sheets"
	| "transforming"
	| "matching_staff"
	| "checking_results"
	| "ready";
export type OnboardingJobItemStatus = "queued" | "running" | "succeeded" | "failed" | "cancelled";
export type OnboardingJobInputKind = "nurses" | "shifts" | "template";
export type DataLoadRosterAction = "create" | "update";

export type DataLoadJobConfiguration = {
	allow_duplicates: boolean;
	min_start_date: string | null;
	roster_actions: Array<DataLoadRosterAction | null>;
};

export type OnboardingJobFailedTab = {
	name: string;
	error: string;
};

export type OnboardingJobItem = {
	id: string;
	ordinal: number;
	input_kind: OnboardingJobInputKind;
	display_filename: string;
	transform: boolean;
	status: OnboardingJobItemStatus;
	stage: OnboardingJobStage;
	failed_tabs: OnboardingJobFailedTab[];
	row_count: number | null;
	prompt_history_id: string | null;
	error_code: string | null;
	user_error_key: string | null;
	created_at: string;
	started_at: string | null;
	finished_at: string | null;
};

export type OnboardingJob = {
	id: string;
	department_id: string;
	requested_by_user_id: string | null;
	job_type: OnboardingJobType;
	origin: OnboardingJobOrigin;
	source_job_id: string | null;
	status: OnboardingJobStatus;
	stage: OnboardingJobStage;
	completed_items: number;
	total_items: number;
	attempt_count: number;
	max_attempts: number;
	error_code: string | null;
	user_error_key: string | null;
	created_at: string;
	updated_at: string;
	started_at: string | null;
	finished_at: string | null;
	cancel_requested_at: string | null;
	available_at: string;
	expires_at: string;
	artifacts_deleted_at: string | null;
	configuration: DataLoadJobConfiguration | null;
	items: OnboardingJobItem[];
};

export type CreateCsvTransformJobItem = {
	input_kind: OnboardingJobInputKind;
	csv_instructions: string;
	tab_instructions: Record<string, string>;
	sheet_name: string | null;
	transform: boolean;
};

/** Common args for the six real `/management_center/multi/*` endpoints (Stage 1 backend contract). */
type MultiFacilityBaseArgs = {
	departmentIds: string[];
	startDate: DateTime;
	endDate?: DateTime;
	isActual?: boolean;
	/** Sent only when overriding the server default (false) — mirrors the single-mode serialization. */
	excludeHolidays?: boolean;
};

/** shift-pickups: `exclude_agency` mirrors the single-mode "show agency pickups" toggle (server default true). */
type MultiFacilityPickupsArgs = MultiFacilityBaseArgs & {
	excludeAgency?: boolean;
};

/** ppd-stats / hppd-stats / cost-stats / kpi-summary additionally accept the value-shaping settings. */
type MultiFacilityStatsArgs = MultiFacilityBaseArgs & {
	includeLateScheduled?: boolean;
	otMode?: OtMode;
};

/** ppd-stats / cost-stats / kpi-summary additionally accept the wage-sensitive pass-throughs. */
type MultiFacilityWageArgs = MultiFacilityStatsArgs & {
	laborCost?: boolean;
	estimateMissingWage?: boolean;
	/** Performance matrix only: request HPPD without returning PPD while wage reveal is off. */
	includePpd?: boolean;
};

const BULK_NURSE_JOBS_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Serializes the shared multi-facility request body (camelCase, matching `MultiFacilityRequest`).
 * These endpoints are reads, but they are sent as POST with a JSON body because `departmentIds`
 * would overflow the URL; each endpoint reads only what it needs. Omitted optional values
 * (undefined) are dropped by JSON serialization, so the backend applies its single-mode defaults —
 * same result as before. The performance endpoint additionally uses `includePpd` as a client-side
 * wage-reveal capability while always retaining HPPD.
 */
const toMultiFacilityBody = ({
	departmentIds,
	startDate,
	endDate,
	isActual,
	laborCost,
	estimateMissingWage,
	includeLateScheduled,
	otMode,
	excludeHolidays,
	excludeAgency,
	includePpd,
}: MultiFacilityWageArgs & MultiFacilityPickupsArgs) => ({
	departmentIds,
	startDate: startDate.toFormat("yyyy-MM-dd"),
	endDate: endDate ? endDate.toFormat("yyyy-MM-dd") : undefined,
	isActual,
	laborCost,
	estimateMissingWage,
	includeLateScheduled,
	otMode,
	excludeHolidays,
	excludeAgency,
	includePpd,
});

/** The bytes route of a stored CV; its `/text` sibling serves the same file as text. */
const careersCvFilePath = (source: CareersCvSource): string =>
	source.kind === "application" ? `/careers/applications/${source.id}/cv` : `/careers/cv-uploads/${source.id}/file`;

const withConvertedUserInfoDates = (response: AxiosResponse<UserInfo>): AxiosResponse<UserInfo> => {
	const { departments, ...userInfoRest } = response.data;
	// `convertIsoDates` is untyped; it returns the same shape with its date strings parsed.
	response.data = {
		...(convertIsoDates(userInfoRest) as typeof userInfoRest),
		departments: departments.map<Department>((d) => convertIsoDates(d, d.timezone) as typeof d),
	};
	return response;
};

export const backendService = {
	getUserInfo: async (): Promise<AxiosResponse<UserInfo>> => withConvertedUserInfoDates(await axiosInstance.get<UserInfo>("/users/user_info")),
	/**
	 * User info as `accessToken` sees it, before that token is installed, with the full config loaded for
	 * `departmentId` — the server loads it for one department only. `_retry` makes a 401 final: replaying
	 * it on the installed token would return the user info of the department being left.
	 */
	getUserInfoForToken: async (accessToken: string, departmentId: string): Promise<AxiosResponse<UserInfo>> =>
		withConvertedUserInfoDates(
			await axiosInstance.get<UserInfo>("/users/user_info", {
				params: { department_id: departmentId },
				headers: { Authorization: `Bearer ${accessToken}` },
				// `_retry` is the response interceptor's own marker; axios has no typed slot for it.
				_retry: true,
			} as AxiosRequestConfig),
		),
	publish: (departmentId: string, updateRequestBody: PublishRequestBody) =>
		axiosInstance.post(`/staffing/publish?department_id=${departmentId}`, updateRequestBody),
	postShift: (body: PostShiftRequestBody, params: PostShiftRequestParams) => axiosInstance.post<Request>("/staffing/open-shift", body, { params }),
	patchShift: (shiftId: string, neededNurses: number) =>
		axiosInstance.patch<{ message: string; agency_posting_failures?: AgencyPostingFailure[] }>(`/staffing/open-shift/${shiftId}/needed-nurses`, {
			needed_nurses: neededNurses,
		}),
	updateShiftJob: (shiftId: string, jobId: string, isActual: boolean) =>
		axiosInstance.patch<{ message: string; jobId: string; excludeFromPpd: boolean }>(`/staffing/shift/${shiftId}/job`, { job_id: jobId, is_actual: isActual }),
	getCommandCenterNurses: (departmentId: string) => axiosInstance.get("/command_center/nurses", { params: { departmentId } }),
	sendChatBotMessage: (chatbotReqeust: ChatBotRequest) => axiosInstance.post("/chat-bot/assist", chatbotReqeust),
	chatbotConversations: () =>
		axiosInstance.get<Array<{ sessionId: string; userId: string; message: string; timestamp: string; role: string }>>("/chat-bot/conversations"),
	chatbotAllConversations: () =>
		axiosInstance.get<Array<{ sessionId: string; userId: string; message: string; timestamp: string; role: string }>>("/chat-bot/conversations/all"),
	chatbotSessionConversations: (sessionId: string) => axiosInstance.get(`/chat-bot/conversations/${sessionId}`),
	sendUserGuideMessage: (message: string, sessionId: string) =>
		axiosInstance.post<ChatbotMessage>("/chat-bot/assist/user-guide", { message, session_id: sessionId }),
	updateAnnieAnswerFeedback: (conversationId: number, feedback: AnnieFeedbackRequest) =>
		axiosInstance.put<AnnieFeedbackResponse>(`/annie/messages/${conversationId}/feedback`, feedback),
	getAnnieAvatar: () => axiosInstance.get<AnnieAvatarSettings>("/annie/avatar"),
	// The wire body is the design itself with `name` beside it, so callers that send a bare design keep working.
	putAnnieAvatar: ({ avatar, name }: AnnieAvatarSaveRequest) =>
		axiosInstance.put<AnnieAvatarSettings>("/annie/avatar", avatar === null ? { kind: AnnieAvatarSaveKind.Standard, name } : { ...avatar, name }),
	deleteAnnieAvatar: () => axiosInstance.delete<AnnieAvatarSettings>("/annie/avatar"),
	putAnnieAvatarVisibility: (hidden: boolean) => axiosInstance.put<AnnieAvatarSettings>("/annie/avatar/visibility", { hidden }),

	deleteConversation: (sessionId: string) => axiosInstance.delete(`/chat-bot/conversations/${sessionId}`),
	deleteShift: (shiftId: string, departmentId: string) =>
		axiosInstance.delete<{ message: string; agency_posting_failures?: AgencyPostingFailure[] }>(`/staffing/open-shift/${shiftId}`, {
			params: { department_id: departmentId },
		}),
	postRequestToAgency: (
		requestId: string,
		departmentId: string,
		body: { agency_source: AgencySource[]; sub_role?: NurseRole; post_with_delay?: boolean; agency_post_delay_hours?: number },
	) =>
		axiosInstance.post<{ message: string; scheduled?: boolean; agency_posting_failures?: AgencyPostingFailure[] }>(
			`/staffing/post-request-to-agency/${requestId}`,
			body,
			{
				params: { department_id: departmentId },
			},
		),
	updateAgencyPostDelay: (requestId: string, departmentId: string, agencyPostDelayHours: number) =>
		axiosInstance.put<{ message: string }>(
			`/staffing/request-agency-post-delay/${requestId}`,
			{ agency_post_delay_hours: agencyPostDelayHours },
			{ params: { department_id: departmentId } },
		),
	updateCensus: (censusData: UpdateCensusData) => axiosInstance.post("/staffing/update_census", censusData),
	mergeRequests: (body: {
		source_request_id: string;
		target_request_id: string;
		department_id: string;
		changed_request_has_responses: boolean;
		needed_nurses: number;
		source: ChangesSource;
	}) => axiosInstance.post("/staffing/merge-requests", body),
	createDepartment: (body: {
		// jobShiftTimes is a job-id-keyed map on the form but an array on the wire — see updateGeneralForm.
		config: Omit<GeneralFormData, "week1FirstSunday" | "departmentType" | "staffScheduleDisplayUntilDate" | "jobShiftTimes" | "jobPpdOverrides"> & {
			week1FirstSunday: string;
			type: string;
			/** `yyyy-MM-dd`; `null` = no horizon, staff see the whole schedule. */
			staffScheduleDisplayUntilDate: string | null;
			jobShiftTimes?: { jobId: string; partialBlockShifts: number[]; fullShiftTimes: [start: number, end: number][] }[];
		};
		newUsers: AdminUser[];
		newDepartmentLocations: DepartmentLocation[];
	}) => {
		return axiosInstance.post<{
			config: Department;
			newUsers: AdminUser[];
			newDepartmentLocations: DepartmentLocation[];
		}>("/departments/new", body);
	},
	getAvailableAdminsAccordingToDepartments: (departmentIds: string[]) => {
		return axiosInstance.get<ExistingAdminUser[]>("/departments/available_admins?", {
			params: { department_ids: departmentIds },
			paramsSerializer: (params) => queryString.stringify(params),
		});
	},
	updateGeneralForm: (
		departmentId: string,
		body: {
			// jobShiftTimes is form-shaped (a Record keyed by job id) on GeneralFormData; the API takes
			// the array below. Optional because role mode omits it, which leaves saved rows untouched.
			// The three wage rule tables are omitted: Team → Wage Settings owns them (DAT-905) and this
			// endpoint no longer writes them.
			config: Omit<
				GeneralFormData,
				"week1FirstSunday" | "staffScheduleDisplayUntilDate" | "jobShiftTimes" | "jobPpdOverrides" | "hoursRateConfig" | "overtimeConfig" | "breakRulesConfig"
			> & {
				week1FirstSunday: string;
				/** Omitted when unchanged so a concurrent publish isn't rolled back; `null` clears the horizon. */
				staffScheduleDisplayUntilDate?: string | null;
				jobShiftTimes?: { jobId: string; partialBlockShifts: number[]; fullShiftTimes: [start: number, end: number][] }[];
			};
		},
	) => {
		return axiosInstance.put<GeneralFormData>(`/departments/${departmentId}/config`, body);
	},
	/**
	 * The facilities our Nursa account can see, so the department's Facility ID can be PICKED by name
	 * instead of pasted as a raw id. Support-only (403 otherwise).
	 *
	 * A Nursa outage comes back as a `status`, never as a thrown error: listing facilities is a
	 * convenience on top of a field that must stay typeable, so the caller degrades to manual entry.
	 */
	getNursaFacilities: (departmentId: string) => {
		return axiosInstance.get<NursaFacilitiesResponse>(`/departments/${departmentId}/nursa/facilities`);
	},

	/**
	 * Whether this department's Nursa wiring is actually live. Two vendor round trips, so it is
	 * fetched by the one tab that renders it and deliberately never rides `/user_info`.
	 */
	getNursaReadiness: (departmentId: string) => {
		return axiosInstance.get<NursaReadiness>(`/departments/${departmentId}/nursa/readiness`);
	},
	/**
	 * Search Nursa's whole facility directory by name — the facilities we are NOT connected to
	 * included, which is what makes it useful for onboarding.
	 *
	 * `name` must be at least two characters and `page` at least 1: measured against production,
	 * Nursa answers a missing `name` with HTTP 500 and `page=0` with a 400, so the caller gates
	 * rather than letting our own bad request read as a Nursa outage.
	 */
	searchNursaFacilities: (departmentId: string, name: string, page: number) => {
		return axiosInstance.get<NursaFacilitySearchResponse>(`/departments/${departmentId}/nursa/facility-search`, { params: { name, page } });
	},
	/**
	 * Adds the department's saved Nursa facility id to our Nursa webhook registration, so Nursa
	 * starts notifying us about that facility's shifts. Support-only, and idempotent — a facility
	 * already on the registration comes back as `already_subscribed` without a write.
	 * The facility id is not sent: the backend reads the persisted one off the department.
	 */
	registerNursaFacilityWebhook: (departmentId: string) => {
		return axiosInstance.post<NursaWebhookSubscriptionResult>(`/departments/${departmentId}/nursa-webhook-subscription`);
	},
	/**
	 * Removes a facility the department has stopped using from our Nursa webhook registration.
	 * The id travels in the body because by this point the department row holds the NEW id (or none)
	 * — the abandoned one exists only on the client. Support-only; the server refuses to remove a
	 * facility another department still maps.
	 */
	unregisterNursaFacilityWebhook: (departmentId: string, facilityId: string) => {
		return axiosInstance.post<NursaWebhookSubscriptionResult>(`/departments/${departmentId}/nursa-webhook-subscription/remove`, { facilityId });
	},
	/**
	 * What switching this agency integration off would touch — open postings, already-scheduled agency
	 * nurses, responses still awaiting a decision, and whether the vendor is still talking to us.
	 * Support-only (403 otherwise).
	 *
	 * Read-only and DISPLAY ONLY: the toggle re-derives its own un-post set server-side, so a failure
	 * here must degrade the dialog's detail, never block the deactivation.
	 */
	getAgencyDeactivationImpact: (departmentId: string, source: AgencySource) => {
		return axiosInstance.get<AgencyDeactivationImpact>(`/departments/${departmentId}/agency-integrations/${source}/deactivation-impact`);
	},
	/**
	 * Switches one department's agency integration on or off. Support-only, both agencies, both
	 * directions; `unpostOpenShifts` is read only when switching OFF.
	 *
	 * The response is a discriminated union — branch on `transition`, never on which optional fields
	 * happen to be present.
	 */
	setAgencyIntegrationActive: (departmentId: string, source: AgencySource, body: { active: boolean; unpostOpenShifts: boolean }) => {
		return axiosInstance.put<AgencyIntegrationToggleResult>(`/departments/${departmentId}/agency-integrations/${source}`, body);
	},
	runWageBreakdown: (departmentId: string) => {
		return axiosInstance.post<{ enqueued: boolean; queueId: number | null }>(`/departments/${departmentId}/run-wage-breakdown`);
	},
	recalculateCensus: (params: { departmentId: string; startDate: DateTime; endDate: DateTime }) => {
		return axiosInstance.post<RecalculateCensusResponse>(`/departments/${params.departmentId}/census-recalculations`, null, {
			params: {
				start_date: params.startDate.toFormat("yyyy-MM-dd"),
				end_date: params.endDate.toFormat("yyyy-MM-dd"),
			},
		});
	},
	setHideOverstaffingWarning: (departmentId: string, value: boolean) => {
		return axiosInstance.put<void>(`/departments/${departmentId}/hide-overstaffing-warning`, { value });
	},
	setAgencyScheduledWithoutPunchDays: (departmentId: string, days: number) => {
		return axiosInstance.put<void>(`/departments/${departmentId}/agency-scheduled-without-punch-days`, null, { params: { days } });
	},
	/** `changed` is true only when a value moved, which is exactly when a recalc of every shift is queued. */
	setTrainingOrientationWage: (departmentId: string, body: TrainingOrientationWageBody) => {
		return axiosInstance.put<{ changed: boolean }>(`/departments/${departmentId}/training-orientation-wage`, body);
	},
	/**
	 * DAT-905 · Everything Team → Wage Settings edits, for the department in the URL. Its own read because
	 * `/users/user_info` fully loads only the token's department: every other one arrives with `[]` rules
	 * and the default wage config, which the tab would otherwise show as if it were saved.
	 */
	getWageSettings: (departmentId: string) => {
		return axiosInstance.get<WageSettingsResponse>(`/departments/${departmentId}/wage-settings`);
	},
	/**
	 * DAT-905 · The three wage rule tables, each saved on its own from Team → Wage Settings. The
	 * response carries the rows as STORED (the "all" markers collapsed to [], fresh ids), which the
	 * caller writes back into `chosenDepartment`. `recalcQueued` says whether every shift is being recalculated.
	 */
	setHoursRateConfig: (departmentId: string, configs: HoursRateConfig[]) => {
		return axiosInstance.put<WageRulesConfigResponse<HoursRateConfig>>(`/departments/${departmentId}/hours-rate-config`, { configs });
	},
	setOvertimeConfig: (departmentId: string, configs: OvertimeConfig[]) => {
		return axiosInstance.put<WageRulesConfigResponse<OvertimeConfig>>(`/departments/${departmentId}/overtime-config`, { configs });
	},
	setBreakRulesConfig: (departmentId: string, configs: BreakRulesConfig[]) => {
		return axiosInstance.put<WageRulesConfigResponse<BreakRulesConfig>>(`/departments/${departmentId}/break-rules-config`, { configs });
	},
	updateDepartmentFirstSunday: (departmentId: string, firstSunday: string) => {
		return axiosInstance.put<{ first_sunday: string }>(`/departments/${departmentId}/first-sunday`, { first_sunday: firstSunday });
	},
	fetchAdminUsers: (department_id: string) => {
		return axiosInstance.get<AdminUser[]>("/staffing/admin-users", {
			params: { department_id },
		});
	},
	/** Writes the permission columns only — `adminRoles` has its own endpoint (`setAdminUserRoles`). */
	updateAdminUsers: (department_id: string, body: { new: AdminUserPermissions[]; existing: AdminUserPermissions[] }) => {
		return axiosInstance.post<UpdateAdminUsersResponse>("/staffing/admin-users", body, {
			params: { department_id },
		});
	},
	/**
	 * Which permissions each role grants — this drives the checkbox rules. The matrix is the same for
	 * every facility, but the endpoint checks department access, so the id is needed for authorization
	 * rather than to filter the result.
	 */
	fetchAdminRoleDefaults: (department_id: string) => {
		return axiosInstance.get<AdminRoleDefaults>("/staffing/admin-role-defaults", { params: { department_id } });
	},
	/** Full replace of one user's roles in one department; `[]` clears them. */
	setAdminUserRoles: (department_id: string, user_id: string, roles: AdminRole[]) => {
		return axiosInstance.post<void>(`/staffing/admin-users/${user_id}/roles`, { roles }, { params: { department_id } });
	},
	removeAdminUserFromDepartment: (department_id: string, user_id: string) => {
		return axiosInstance.delete<void>(`/staffing/admin-users/${user_id}`, {
			params: { department_id },
		});
	},
	sendWelcomeEmail: (body: { userId: string; channel: "email" | "sms" }) => {
		return axiosInstance.post<void>("/team/user-welcome", body);
	},
	supportSetPassword: (body: { userId: string; newPassword: string }) => {
		return axiosInstance.post<void>("/team/support/set-password", body);
	},
	sendNurseWelcome: (params: { nurseId: number; departmentId: string }) => {
		return axiosInstance.post("/team/nurse-welcome", {
			nurseId: params.nurseId,
			departmentId: params.departmentId,
			channel: "email",
		});
	},
	/** Per-nurse send readiness (mobile/email/language/last send) for the bulk resend dialog. */
	getResendWelcomePreflight: (departmentId: string) =>
		axiosInstance.get<ResendWelcomePreflightResponse>("/team/resend-welcome/preflight", { params: { department_id: departmentId } }),
	/** One request for the whole batch — each send replaces that person's previous link, so it must never be retried blind. */
	sendBulkNurseWelcome: (body: { departmentId: string; nurseIds: number[]; customMessage: string | null }) =>
		axiosInstance.post<BulkNurseWelcomeResponse>("/team/bulk-nurse-welcome", body),
	sendNurseReset: ({ emailOrPhoneNumber, sendFromMultipleCommunications = true }: PasswordResetRequestBody) => {
		return axiosInstance.post("/users/forgot-password", {
			emailOrPhoneNumber,
			sendFromMultipleCommunications,
		});
	},
	fetchStaffCommunicationThreads: (department_id: string) => {
		const searchParams = new URLSearchParams({
			department_id,
		});
		return axiosInstance.get<CombinedMessagesResponse>(`/chat/messages/v2?${searchParams.toString()}`);
	},
	fetchStaffCommunicationThreadSummaries: (department_id: string) => {
		return axiosInstance.get<StaffCommunicationSummaryResponse>("/chat/thread-summaries", {
			params: { department_id },
		});
	},
	fetchStaffCommunicationThreadHistory: (department_id: string, thread_user_id: string, limit: number, offset: number) => {
		return axiosInstance.get<StaffCommunicationHistoryResponse>(`/chat/threads/${thread_user_id}/messages`, {
			params: { department_id, limit, offset },
		});
	},
	postStaffCommunicationMessage: (department_id: string, userIds: string[], message: string, addAppLink?: boolean) => {
		return axiosInstance.post<PostChatMessageResult[]>(`/chat/messages?department_id=${department_id}`, {
			message,
			userIds,
			addAppLink,
		});
	},
	updateThreadReadStatus: (department_id: string, thread_user_id: string) => {
		const searchParams = new URLSearchParams({
			department_id,
			thread_user_id,
		});
		return axiosInstance.post<{ success: boolean }>(`/chat/thread-read-statuses?${searchParams.toString()}`);
	},
	getMessagesStatuses: (department_id: string, messages: string[]) => {
		return axiosInstance.post<UserSentMessage[]>(`/chat/messages_by_ids?department_id=${department_id}`, {
			messages,
		});
	},
	// --- Hiring Bot chat (DAT-488). Gated server-side by the `hiring_tab` flag. --- //
	fetchHiringThreads: () => {
		return axiosInstance.get<HiringThreadsResponse>("/hiring-chat/threads");
	},
	fetchHiringMessages: (candidateId: string) => {
		return axiosInstance.get<HiringConversationResponse>(`/hiring-chat/threads/${candidateId}/messages`);
	},
	postHiringMessage: (candidateId: string, body: string, templateKey?: string) => {
		return axiosInstance.post<HiringMessage>(`/hiring-chat/threads/${candidateId}/messages`, { body, templateKey });
	},
	messageHiringInterviewer: (processId: string, message: string, files: File[] = []) => {
		// Multipart so the message can carry MMS attachments; the backend enforces the 600 KB/file + 5 MB total caps.
		const form = new FormData();
		form.append("message", message);
		for (const file of files) form.append("files", file);
		return axiosInstance.post<{ sent_count: number; recipients: string[] }>(`/hiring-chat/processes/${processId}/message-interviewer`, form);
	},
	fetchHiringTemplates: (candidateId: string, interviewId: string) => {
		return axiosInstance.get<HiringTemplatesResponse>(`/hiring-chat/threads/${candidateId}/templates`, {
			params: { interview_id: interviewId },
		});
	},
	markHiringThreadRead: (candidateId: string) => {
		return axiosInstance.post<void>(`/hiring-chat/threads/${candidateId}/read`);
	},
	// Distinct positions in the caller's scope — the Position single-select options (Add/Edit candidate).
	fetchHubPositions: () => {
		return axiosInstance.get<string[]>("/hiring-chat/positions");
	},
	fetchHiringFacilities: () => {
		return axiosInstance.get<HiringFacilitiesResponse>("/hiring-chat/facilities");
	},
	fetchHiringSyncStatus: () => {
		return axiosInstance.get<SyncStatusResponse>("/hiring-chat/sync-status");
	},
	syncHiringNow: () => {
		return axiosInstance.post<void>("/hiring-chat/sync");
	},
	importHiringWorkbook: (file: File) => {
		const formData = new FormData();
		formData.append("file", file);
		return axiosInstance.post<void>("/hiring-chat/import", formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
	},
	setCandidateStatus: (candidateId: string, status: string, sendMessage: boolean) => {
		return axiosInstance.post<{ status: string }>(`/hiring-chat/threads/${candidateId}/status`, { status, sendMessage });
	},
	setCandidateCancelReason: (candidateId: string, cancelReason: string | null) => {
		return axiosInstance.post<{ status: string }>(`/hiring-chat/threads/${candidateId}/cancel-reason`, { cancelReason });
	},
	patchInterviewTime: (interviewId: string, interviewTime: string | null, resetStatus: boolean) => {
		return axiosInstance.patch<void>(`/hiring-chat/interviews/${interviewId}`, { interviewTime, resetStatus });
	},

	// --- Hiring Hub CRUD/API (Hiring Bot v1, Phase 4/5). Shares the `/hiring-chat` prefix + `hiring_tab`
	// gate, then layers hiring-role scope. GET responses are snake_case (no aliases on the response models);
	// request bodies/query params are camelCase. --- //
	fetchHubAccess: () => {
		return axiosInstance.get<HubAccess>("/hiring-chat/access");
	},
	// UNgated (no hiring_tab / role requirement) — returns whether the user gets the Hiring tab (flag OR
	// hiring role), so the nav + route gate can ask for any user without provoking a 403.
	fetchHubTabAccess: () => {
		return axiosInstance.get<HubTabAccess>("/hiring-chat/tab-access");
	},
	fetchInterviewSchedule: () => {
		return axiosInstance.get<HubScheduleResponse>("/hiring-chat/interview-schedule");
	},
	// The FIRST signal-aware call in this file. The candidates grid loads every page inside one TanStack
	// `queryFn`, so an abort (a filter change, or the row-interviewer mutation's `cancelQueries`) has to reach
	// the request that is actually in flight — without it a superseded load keeps issuing pages to completion.
	// Threaded per-call rather than set on `axiosInstance`: every other caller here is a single request whose
	// cancellation behavior would otherwise change silently.
	fetchHubCandidates: (filters: HubCandidateFilters, signal?: AbortSignal) => {
		return axiosInstance.get<HubCandidatesResponse>("/hiring-chat/candidates", {
			params: buildHubCandidateParams(filters),
			// `arrayFormat: "none"` (query-string's default) serializes `departmentIds` as a repeatable
			// `departmentIds=a&departmentIds=b` param, which the backend reads as a list.
			paramsSerializer: (params) => queryString.stringify(params),
			signal,
		});
	},
	fetchHubCandidate: (candidateId: string) => {
		return axiosInstance.get<HubCandidateProfile>(`/hiring-chat/candidates/${candidateId}`);
	},
	createHubCandidate: (body: CreateCandidateRequest) => {
		return axiosInstance.post<HubCandidateProfile>("/hiring-chat/candidates", body);
	},
	updateHubCandidate: (candidateId: string, body: UpdateCandidateRequest) => {
		return axiosInstance.patch<void>(`/hiring-chat/candidates/${candidateId}`, body);
	},
	deleteHubCandidate: (candidateId: string, notify = false) => {
		return axiosInstance.delete<void>(`/hiring-chat/candidates/${candidateId}`, { params: { notify } });
	},
	updateHubProcess: (processId: string, body: UpdateProcessRequest) => {
		return axiosInstance.patch<void>(`/hiring-chat/processes/${processId}`, body);
	},
	setHubPipelineStatus: (processId: string, body: UpdatePipelineStatusRequest) => {
		return axiosInstance.patch<void>(`/hiring-chat/processes/${processId}/pipeline-status`, body);
	},
	createHubProcessNote: (processId: string, body: ProcessNoteRequest) => {
		return axiosInstance.post<HubProcessNote>(`/hiring-chat/processes/${processId}/notes`, body);
	},
	// Note ids are globally unique, so edit/delete key off the note alone — the backend resolves its owning
	// process to authorize the write.
	updateHubProcessNote: (noteId: string, body: ProcessNoteRequest) => {
		return axiosInstance.patch<void>(`/hiring-chat/notes/${noteId}`, body);
	},
	deleteHubProcessNote: (noteId: string) => {
		return axiosInstance.delete<void>(`/hiring-chat/notes/${noteId}`);
	},
	createHubInterview: (processId: string, body: UpdateInterviewRequest) => {
		return axiosInstance.post<CreatedInterviewResponse>(`/hiring-chat/processes/${processId}/interviews`, body);
	},
	updateHubInterview: (processId: string, interviewId: string, body: UpdateInterviewRequest) => {
		return axiosInstance.patch<void>(`/hiring-chat/processes/${processId}/interviews/${interviewId}`, body);
	},
	fetchHubInterviewers: (departmentId: string) => {
		return axiosInstance.get<HubDepartmentUsersResponse>(`/hiring-chat/departments/${departmentId}/interviewers`);
	},
	// The full interviewer roster across the caller's visible departments (Interviewers tab "All departments").
	fetchAllInterviewers: () => {
		return axiosInstance.get<HubInterviewersResponse>("/hiring-chat/interviewers");
	},
	// The departments the caller manages that have at least one interviewer, for the Interviewers-tab
	// department selector (so it only lists departments that actually have interviewers to show).
	fetchInterviewerDepartments: () => {
		return axiosInstance.get<HiringFacilityOption[]>("/hiring-chat/interviewer-departments");
	},
	fetchInterviewerDepartmentOptions: () => {
		return axiosInstance.get<HiringFacilityOption[]>("/hiring-chat/interviewer-department-options");
	},
	fetchCandidateDepartmentOptions: () => {
		return axiosInstance.get<HiringFacilityOption[]>("/hiring-chat/candidate-department-options");
	},
	addHubInterviewer: (departmentId: string, body: { userId: string }) => {
		return axiosInstance.post<{ user_id: string; first_name: string; email: string | null; phone: string; department_id: string; created: boolean }>(
			`/hiring-chat/departments/${departmentId}/interviewers`,
			body,
		);
	},
	// Apply an Edit-Interviewer-Departments save in ONE transaction (all-or-nothing) — grant the role in
	// addDepartmentIds, revoke it (unassigning upcoming interviews) in removeDepartmentIds. Replaces the
	// per-department add/remove loop so a mid-change failure can't leave a partially-applied set.
	setInterviewerDepartments: (userId: string, addDepartmentIds: string[], removeDepartmentIds: string[]) => {
		return axiosInstance.post<void>(`/hiring-chat/interviewers/${userId}/set-departments`, { addDepartmentIds, removeDepartmentIds });
	},
	// Fire the interviewer's combined "you've been added/removed" SMS ONCE after an Add/Edit save — one text
	// per direction listing all affected facilities (avoids one SMS per department).
	notifyInterviewerRoleChange: (userId: string, addedDepartmentIds: string[], removedDepartmentIds: string[], canceledCandidateNames: string[] = []) => {
		return axiosInstance.post<void>(`/hiring-chat/interviewers/${userId}/notify-role-change`, {
			addedDepartmentIds,
			removedDepartmentIds,
			canceledCandidateNames,
		});
	},
	// Typeahead for the Add Interviewer picker: existing department members whose name matches `q`. With a
	// departmentId the search is scoped to that department; without one it spans the recruiter's manageable
	// departments. An empty `q` with no department returns the caller's WHOLE grantable pool (the endpoint is
	// uncapped by design), which is what populates the picker before anyone types.
	searchInterviewerCandidates: (departmentId: string | null, q: string) => {
		return axiosInstance.get<InterviewerCandidate[]>("/hiring-chat/interviewer-candidates", {
			params: { departmentId: departmentId ?? undefined, q },
		});
	},
	removeHubInterviewer: (departmentId: string, userId: string) => {
		return axiosInstance.delete<void>(`/hiring-chat/departments/${departmentId}/interviewers/${userId}`);
	},
	// The candidates an interviewer is currently assigned to in one department — shown in the
	// delete-interviewer confirmation so the recruiter sees who becomes unassigned on revoke.
	fetchInterviewerAssignments: (departmentId: string, userId: string) => {
		return axiosInstance.get<HubInterviewerAssignmentsResponse>(`/hiring-chat/departments/${departmentId}/interviewers/${userId}/assignments`);
	},
	fetchAllInterviewerAssignments: (userId: string) => {
		return axiosInstance.get<HubInterviewerAssignmentsResponse>(`/hiring-chat/interviewers/${userId}/assignments`);
	},
	// The departments the caller may add/remove a person's interviewer role in (their manageable
	// memberships), each with the current is_interviewer flag — feeds the Edit Interviewer Departments modal.
	fetchEditableInterviewerDepartments: (userId: string) => {
		return axiosInstance.get<HubEditableDepartmentsResponse>(`/hiring-chat/interviewers/${userId}/editable-departments`);
	},
	uploadHubDocument: (candidateId: string, docType: HubDocType, file: File) => {
		const formData = new FormData();
		formData.append("file", file);
		return axiosInstance.post<void>(`/hiring-chat/candidates/${candidateId}/documents/${docType}`, formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
	},
	getHubDocumentDownloadUrl: (candidateId: string, docType: HubDocType) => {
		return axiosInstance.get<DocumentUrlResponse>(`/hiring-chat/candidates/${candidateId}/documents/${docType}/download`);
	},
	deleteHubDocument: (candidateId: string, docType: HubDocType) => {
		return axiosInstance.delete<void>(`/hiring-chat/candidates/${candidateId}/documents/${docType}`);
	},
	updateDepartmentLocations: (department_id: string, body: { new: DepartmentLocation[]; existing: DepartmentLocation[] }) => {
		return axiosInstance.post<DepartmentLocation[]>("/departments/locations", body, {
			params: { department_id },
		});
	},
	deleteDepartmentLocations: (department_id: string, id: string) => {
		return axiosInstance.delete(`/departments/locations/${id}`, {
			params: { department_id },
		});
	},
	fetchNurseProfile: (department_id: string, nurse_id: string) => {
		const searchParams = new URLSearchParams({ department_id, nurse_id });
		return axiosInstance.get<Nurse>(`/team/nurse?${searchParams.toString()}`);
	},
	fetchNurseProfileImage: (nurse_id: string) => {
		return axiosInstance.get<string>(`/images/get_profile_image_base64/${nurse_id}`);
	},
	addDayOffPreference: (departmentId: string, body: CreateSpecificPreferenceOptions) => {
		return axiosInstance.post(`/team/departments/${departmentId}/employee_specific_preferences`, body);
	},
	updateDayOffPreference: (
		departmentId: string,
		nurseId: number,
		dayOffId: string,
		body: CreateSpecificPreferenceOptions,
	): Promise<{ data: UpdateSpecificPreferenceResult }> => {
		return axiosInstance.put(`/team/departments/${departmentId}/nurse/${nurseId}/employee_specific_preferences/${dayOffId}`, body);
	},
	// Server-side preview of what saving a time off over [windowStartIso, windowEndIso) would do to
	// the nurse's schedule. ISO strings in, Luxon `DateTime`s out (the response interceptor converts).
	getTimeOffImpact: (departmentId: string, nurseId: number, windowStartIso: string, windowEndIso: string) => {
		return axiosInstance.get<TimeOffImpactResponse>(`/team/departments/${departmentId}/nurse/${nurseId}/time-off-impact`, {
			params: { window_start: windowStartIso, window_end: windowEndIso },
		});
	},
	// Whether deactivating on `deactivationDate` would remove shifts or recurring templates — the same rows the
	// deactivate endpoint deletes, so the dialog's warnings match what happens.
	getDeactivationImpact: (departmentId: string, nurseId: number, deactivationDate: string) =>
		axiosInstance.get<DeactivationImpactResponse>(`/team/departments/${departmentId}/nurse/${nurseId}/deactivation-impact`, {
			params: { deactivation_date: deactivationDate },
		}),
	getTeamRoster: (departmentId: string) => axiosInstance.get<TeamNurse[]>(`/team/departments/${departmentId}/roster`),
	getTeamStats: (departmentId: string) => axiosInstance.get<TeamStatsResponse>(`/team/departments/${departmentId}/stats`),
	getPtoTrimmedSegments: (departmentId: string, nurseId: number, windowStartIso: string, windowEndIso: string) => {
		return axiosInstance.get<PtoTrimmedSegmentsResponse>(`/team/departments/${departmentId}/nurse/${nurseId}/pto-trimmed-segments`, {
			params: { window_start: windowStartIso, window_end: windowEndIso },
		});
	},
	deleteDayOffInProfile: (nurseId: number, departmentId: string, dayOffId: string, restoreRecurringShifts?: boolean, restoreTrimmedShifts?: boolean) => {
		return axiosInstance.delete(`/team/departments/${departmentId}/nurse/${nurseId}/employee_specific_preferences/${dayOffId}`, {
			data: { source: ChangesSource.Profile, restoreRecurringShifts, restoreTrimmedShifts },
		});
	},
	postNewNurseProfile: (nurseObjectToSubmit: NurseProfileToSubmit, shouldCreateUser: boolean, shouldNotify: boolean) => {
		return axiosInstance.post(`/team/nurse-v2?source=${ChangesSource.Profile}`, {
			nurse: nurseObjectToSubmit,
			shouldCreateUser,
			shouldNotify: shouldCreateUser && shouldNotify,
		});
	},
	checkDuplicateNurse: (firstName: string, lastName: string, departmentId: string) => {
		return axiosInstance.post<{ matchedNurses: { id: number; firstName: string; lastName: string; createdAt: string | null }[] }>(
			"/team/nurse/check-duplicate",
			{ firstName, lastName, departmentId },
		);
	},
	putUpdatedNurseProfile: (updatedNurseObject: NurseProfileToSubmit, departmentId: string) => {
		return axiosInstance.put(`/team/nurse?department_id=${departmentId}`, updatedNurseObject);
	},
	updateNurseJobs: (
		departmentId: string,
		nurseId: number,
		payload: {
			new_job_ids: string[];
			reassign_shifts_to_primary: boolean;
		},
	) => {
		return axiosInstance.put(`/team/departments/${departmentId}/nurse/${nurseId}/jobs`, payload);
	},
	/** For each job, whether the employee gets "New open shift" notifications. */
	getNurseJobNotifications: (departmentId: string, nurseId: number) =>
		axiosInstance.get<NurseJobNotificationsResponse>(`/team/departments/${departmentId}/nurse/${nurseId}/job-notifications`),
	/** Saves only the jobs sent. Other jobs keep their value. */
	updateNurseJobNotifications: (departmentId: string, nurseId: number, body: JobNotificationChangesRequest) =>
		axiosInstance.put<NurseJobNotificationsResponse>(`/team/departments/${departmentId}/nurse/${nurseId}/job-notifications`, body),
	/** Employees in the department with at least one silent job. */
	getDepartmentSilentJobs: (departmentId: string) =>
		axiosInstance.get<DepartmentSilentJobsResponse>(`/team/departments/${departmentId}/job-notifications/silent`),
	/** The signed-in employee's jobs and whether each one notifies. */
	getMyJobNotifications: () => axiosInstance.get<MyJobNotificationsResponse>("/settings/job-notifications"),
	/** Turns one job's open-shift notifications on or off on one of the signed-in employee's records. */
	setMyJobNotification: (body: SetMyJobNotificationRequest) => axiosInstance.put<MyJobNotificationsResponse>("/settings/job-notifications", body),
	/** Dry run of a bulk job change: who would change, who has future shifts in the way, who is skipped and why. */
	previewBulkNurseJobs: (departmentId: string, body: BulkNurseJobsRequest) =>
		axiosInstance.post<BulkNurseJobsPreview>(`/team/departments/${departmentId}/nurses/jobs/preview`, body),
	updateBulkNurseJobs: (departmentId: string, body: BulkNurseJobsApplyRequest) =>
		axiosInstance.put<BulkNurseJobsResult>(`/team/departments/${departmentId}/nurses/jobs`, body, {
			// The server changes each employee in turn (up to 1000, each possibly moving shifts), so a
			// full-roster batch can outlast the default timeout while still succeeding server-side.
			timeout: BULK_NURSE_JOBS_TIMEOUT_MS,
		}),
	applyPrimaryJobToShifts: (departmentId: string, nurseId: number, fromDate: string) => {
		return axiosInstance.post<{ scheduled_shifts_updated: number; recurring_shifts_updated: number }>(
			`/team/departments/${departmentId}/nurse/${nurseId}/apply-primary-job-to-shifts`,
			{ from_date: fromDate },
		);
	},
	updateNurseShiftWage: (departmentId: string, wage: number, date: string, nurseIds: number[]) =>
		axiosInstance.put("/shifts/wage", { department_id: departmentId, wage, date, nurse_ids: nurseIds }),
	postProfileImage: (nurseId: number, fileImage: File) => {
		if (nurseId && fileImage) {
			// Generate a random unique name to the file
			const uniqueName = `${Math.random().toString(36).substring(2)}_${fileImage.name}`;
			const imageData = new FormData();
			imageData.append("file", fileImage, uniqueName);

			return axiosInstance.post(`/images/upload/${nurseId}`, imageData, {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			});
		}
		return Promise.reject(new Error("Nurse ID or file image is missing"));
	},

	syncShifts: (params: { departmentId: string; file?: File; startDate?: DateTime; endDate?: DateTime }) => {
		const formData = new FormData();
		if (params.file) {
			formData.append("file", params.file);
		}
		return axiosInstance.post<ExternalShiftDiff>("/integrations/sync-shifts", formData, {
			params: {
				department_id: params.departmentId,
				start_date: params.startDate?.toFormat("yyyy-MM-dd"),
				end_date: params.endDate?.toFormat("yyyy-MM-dd"),
			},
			headers: { "Content-Type": "multipart/form-data" },
		});
	},

	syncEmployees: (params: { departmentId: string; startDate?: DateTime; endDate?: DateTime }) => {
		return axiosInstance.post<ExternalEmployeeDiff>("/integrations/sync-employees", null, {
			params: {
				department_id: params.departmentId,
				start_date: params.startDate?.toFormat("yyyy-MM-dd"),
				end_date: params.endDate?.toFormat("yyyy-MM-dd"),
			},
		});
	},

	syncPtos: (params: { departmentId: string }) => {
		return axiosInstance.post<SyncScheduledResponse>("/integrations/sync-pto", null, {
			params: {
				department_id: params.departmentId,
			},
		});
	},

	resyncIntegration: (params: { departmentId: string; source: ExternalSource; type: HrisIntegrationType; folder?: string; file?: string }) => {
		return axiosInstance.post<SyncScheduledResponse>("/integrations/resync", null, {
			params: {
				department_id: params.departmentId,
				source: params.source,
				type: params.type,
				folder: params.folder,
				file: params.file,
			},
		});
	},

	syncNursePto: (params: { nurseId: number; departmentId: string }) => {
		return axiosInstance.post<ExternalTimeOffDiff>("/integrations/sync-pto/nurse", {
			nurse_id: params.nurseId,
			department_id: params.departmentId,
		});
	},

	postRecurringShift: (request: CreateRecurringShiftRequest) => {
		return axiosInstance.post<ResultOfRecurringCreation>("/recurring_shift", request);
	},
	getRecurringShifts: (departmentId: string, nurse_id: number) => {
		return axiosInstance.get<RecurringShift[]>(`/recurring_shift/${departmentId}/${nurse_id}`);
	},
	getRecurringShift: (departmentId: string, nurse_id: number, recurring_shift_id: number) => {
		return axiosInstance.get<RecurringShift | null>(`/recurring_shift/${departmentId}/${nurse_id}/${recurring_shift_id}`);
	},
	putRecurringShift: (request: UpdateRecurringShiftRequest) => {
		return axiosInstance.put<RecurringShift>("/recurring_shift", request);
	},
	deleteRecurringShift: (request: DeleteRecurringShiftRequest) => {
		return axiosInstance.delete("/recurring_shift", {
			data: request,
		});
	},
	calculateRecurringShiftDailyHours: (request: CreateRecurringShiftRequest, startDate: string, endDate?: string) => {
		return axiosInstance.post<DateTime[]>("/recurring_shift/calculate-daily-hours", request, {
			params: { start_date: startDate, end_date: endDate },
		});
	},
	getRecurringShiftOvertime: (departmentId: string, startDate: string, numWeeks: number) =>
		axiosInstance.get<RecurringOvertimeMap>(`/recurring_shift/${departmentId}/overtime`, {
			params: { start_date: startDate, num_weeks: numWeeks },
		}),
	fetchNurseNameExists: (department_id: string, first_name: string, last_name: string, nurse_id?: number) => {
		const searchParams = new URLSearchParams({
			department_id,
			first_name,
			last_name,
			...(nurse_id ? { nurse_id: nurse_id.toString() } : {}),
		});
		return axiosInstance.get<boolean>(`/team/nurse-name-exists?${searchParams.toString()}`);
	},
	fetchNurseNameExistsV2: (department_id: string, first_name: string, last_name: string, nurse_id?: number) => {
		const searchParams = new URLSearchParams({
			department_id,
			first_name,
			last_name,
			...(nurse_id ? { nurse_id: nurse_id.toString() } : {}),
		});
		return axiosInstance.get<{ exists: boolean; nurse_id: number | null }>(`/team/nurse-name-exists-v2?${searchParams.toString()}`);
	},
	scheduleNurseDeactivation: (departmentId: string, nurseId: number, deactivationDate: string, postShifts: boolean) => {
		return axiosInstance.put(`/team/departments/${departmentId}/nurse/${nurseId}/deactivate`, null, {
			params: {
				...(deactivationDate && { deactivation_date: deactivationDate }),
				post_shifts: postShifts,
			},
		});
	},
	activateNurse: (departmentId: string, nurseId: number) => {
		return axiosInstance.put(`/team/departments/${departmentId}/nurse/${nurseId}/activate`);
	},
	removeNurseFromDepartment: (departmentId: string, nurseId: number) => {
		return axiosInstance.delete(`/team/departments/${departmentId}/nurse/${nurseId}/remove`);
	},
	setEmployeeStatus: (
		departmentId: string,
		nurseId: number,
		body: { status: SettableEmployeeStatus; status_reason: string | null; start_date: string; end_date: string | null },
	) => {
		return axiosInstance.put(`/team/departments/${departmentId}/nurse/${nurseId}/status`, body);
	},
	clearEmployeeStatus: (departmentId: string, nurseId: number) => {
		return axiosInstance.delete(`/team/departments/${departmentId}/nurse/${nurseId}/status`);
	},
	createOrUpdateCensusOverride: (data: {
		department_id: string;
		date: string;
		shift_type: string;
		override_value: number;
		note?: string;
		source: ChangesSource;
		location_census?: CensusGrouping;
	}) => axiosInstance.post("/departments/census_override", data),
	deleteCensusOverride: (data: { department_id: string; date: string; shift_type: string; source: ChangesSource }) =>
		axiosInstance.delete("/departments/census_override", { data }),
	getRefreshStatusV2: (departmentId: string, clientStoredLastUpdate: GroupedActivityLogClientTimestamps, shouldIgnoreSelfChanges = true) => {
		return axiosInstance.post<GroupedActivityLogChangeResult>("/refresh/get-status-v2", clientStoredLastUpdate, {
			params: {
				department_id: departmentId,
				should_ignore_self_changes: shouldIgnoreSelfChanges,
			},
		});
	},
	appleSSO: (auth: AppleAuthResponse["authorization"]) => {
		const chosenDeptId = localStorageService.getChosenDepartmentID();
		const clientType = Capacitor.isNativePlatform() ? ClientType.MOBILE : ClientType.WEB;
		const url = chosenDeptId ? `/token/sso/apple?department_id=${chosenDeptId}` : "/token/sso/apple";
		return axiosInstance.post<Token>(url, auth, {
			headers: {
				"client-type": clientType,
			},
		});
	},
	googleSSO: (code: string) => {
		const chosenDeptId = localStorageService.getChosenDepartmentID();
		const url = chosenDeptId ? `/token/sso/google?department_id=${chosenDeptId}` : "/token/sso/google";
		return axiosInstance.post<Token>(
			url,
			{ code },
			{
				headers: {
					"client-type": Capacitor.isNativePlatform() ? ClientType.MOBILE : ClientType.WEB,
				},
			},
		);
	},
	getRecommendations: (params: RecommendationParams) =>
		axiosInstance.get<Recommendation[]>("/recommendations", {
			params: {
				department_id: params.departmentId,
				start_date: params.startDate,
				end_date: params.endDate,
			},
		}),

	getAnnieRecommendations: (departmentId: string) =>
		axiosInstance.get<{ recommendations: PickupApprovalItem[] }>("/annie/recommendations", {
			params: { department_id: departmentId },
		}),

	getFeatureFlagsBatch: (flagKeys: string[], departmentId: string) =>
		axiosInstance.get<Record<string, boolean>>("/feature-flags/batch", {
			params: {
				flags: flagKeys.join(","),
				department_id: departmentId,
			},
		}),

	/** SUPPORT-ONLY (403 otherwise). Every flag LaunchDarkly holds, evaluated for this department,
	 * with the targeting metadata behind each value. */
	getFeatureFlagsAdminMetadata: (departmentId: string) =>
		axiosInstance.get<Record<string, FlagAdminMetadata>>("/feature-flags/admin/metadata", {
			params: {
				department_id: departmentId,
			},
		}),

	getCallOffsWithLimit: (params: GetLimitedCallOffs & { departmentId: string }) =>
		axiosInstance.get<CalledOffShiftResponse>("/team/call-offs/limit", {
			params: {
				department_id: params.departmentId,
				page: params.page,
				page_size: params.pageSize,
				...(params.nurseIds && params.nurseIds.length > 0 ? { nurse_ids: params.nurseIds } : {}),
				...(params.fromDate ? { from_date: params.fromDate.toFormat("yyyy-MM-dd") } : {}),
				...(params.toDate ? { to_date: params.toDate.toFormat("yyyy-MM-dd") } : {}),
				...(params.includedMessages && params.includedMessages.length > 0 ? { included_messages: params.includedMessages } : {}),
			},
			paramsSerializer: (queryParams) =>
				queryString.stringify(queryParams, {
					arrayFormat: "none",
				}),
		}),
	cancelNurseCallOffs: (params: CancelCallOffs) =>
		axiosInstance.put<CalledOffShift[]>(
			"/team/cancel-call-offs",
			{
				calledOffShifts: params.calledOffShifts,
			},
			{
				params: {
					department_id: params.departmentId,
				},
			},
		),
	getDepartmentRecurringShiftsForMasterSchedule: (
		departmentId: string,
		startTime: DateTime,
		recurringTypes?: (keyof typeof RecurringShiftRecurrenceType)[],
		perDay = false,
	) => {
		const path = perDay ? `/recurring_shift/per_day/${departmentId}` : `/recurring_shift/${departmentId}`;
		return axiosInstance.get<RecurringShift[]>(path, {
			params: {
				start_time: startTime.toISO(),
				recurring_types: recurringTypes,
			},
			paramsSerializer: (params) => queryString.stringify(params),
		});
	},
	getMasterScheduleCoverageSummary: (departmentId: string, startDate: DateTime, targetMode: CoverageTargetMode) => {
		return axiosInstance.get<CoverageSummaryResponse>(`/master-schedule/${departmentId}/coverage-summary`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				target_mode: targetMode,
			},
		});
	},
	getMasterSchedulePeopleRotation: (departmentId: string, startDate: DateTime) => {
		return axiosInstance.get<PeopleRotationResponse>(`/master-schedule/${departmentId}/people-rotation`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
			},
		});
	},
	updateSingleDepartmentLocation: (department_id: string, body: UpdateSingleDepartmentLocationBody) => {
		return axiosInstance.put<UpdateSingleDepartmentLocationResult>(`/departments/locations/${body.updatedLocation.id}`, body, {
			params: { department_id },
		});
	},
	renameDepartmentLocation: (department_id: string, body: RenameDepartmentLocationBody) => {
		return axiosInstance.post<RenameDepartmentLocationResult>("/departments/locations/rename", body, {
			params: { department_id },
		});
	},
	setDepartmentLocationEmr: (department_id: string, body: SetLocationEmrBody) => {
		return axiosInstance.post<SetLocationEmrResult>("/departments/locations/emr", body, {
			params: { department_id },
		});
	},
	/** Report-only: which of `candidates` a save would refuse as duplicates. Writes nothing. */
	findDuplicateLocationRows: (department_id: string, candidates: DepartmentLocation[]) => {
		const body: FindDuplicateLocationsBody = { candidates };
		return axiosInstance.post<FindDuplicateLocationsResult>("/departments/locations/duplicates", body, {
			params: { department_id },
		});
	},
	deleteDepartmentLocationRows: (department_id: string, ids: string[]) => {
		const body: DeleteDepartmentLocationsBody = { ids };
		return axiosInstance.post<DeleteDepartmentLocationsResult>("/departments/locations/delete", body, {
			params: { department_id },
		});
	},
	getPccRooms: (department_id: string) => {
		return axiosInstance.get<string[]>("/departments/pcc_rooms", {
			params: { department_id },
		});
	},
	getDepartmentHasShifts: (department_id: string) => {
		return axiosInstance.get<DepartmentHasShifts>(`/departments/${department_id}/has-shifts`);
	},
	getCensusPointInTimes: (params: CensusPointInTimesParams) => {
		return axiosInstance.get<CensusPointInTimes>("/staffing/census-point-in-times", {
			params: {
				department_id: params.departmentId,
				date: params.date.toFormat("yyyy-MM-dd"),
			},
		});
	},
	getNurseToRemove: (params: NursesToRemoveParams) => {
		return axiosInstance.get<NurseData[]>("/staffing/nurses-to-remove", {
			params: {
				department_id: params.departmentId,
				date: params.date.toFormat("yyyy-MM-dd"),
			},
		});
	},
	getRequestNursesToApprove: (params: RequestApprovalNursesParams) => {
		return axiosInstance.get<NurseData[]>("/staffing/request-nurses-to-approve", {
			params: {
				department_id: params.departmentId,
				request_id: params.requestId,
			},
		});
	},
	createOrUpdateMultipleRecurringShifts: (request: UpdateMultipleRecurringShiftRequest) => {
		return axiosInstance.put<UpdateMultipleRecurringShiftRequestResult>("/recurring_shift/multiple", request);
	},
	updateRequest: (departmentId: string, request: Request) => {
		return axiosInstance.put<Request>("/requests", request, {
			params: { department_id: departmentId },
		});
	},
	setRequestFlatBonus: (departmentId: string, requestId: string, value: number) =>
		axiosInstance.put<void>(`/requests/${requestId}/flat-bonus`, { value }, { params: { department_id: departmentId } }),
	deleteRequestFlatBonus: (departmentId: string, requestId: string) =>
		axiosInstance.delete<void>(`/requests/${requestId}/flat-bonus`, { params: { department_id: departmentId } }),
	resetPassword: ({ token, newPassword }: { token: string; newPassword: string }) => {
		return axiosInstance.post<Token>("/users/reset-password", {
			token,
			new_password: newPassword,
		});
	},
	checkUsernameStatus: (username: string) => {
		return axiosInstance.post<{ user_exists: boolean; is_initialized: boolean; has_valid_pending_setup: boolean; setup_id?: number }>("/users/check-username", {
			username,
		});
	},
	resetPasswordFromApp: ({ newPassword, username }: { newPassword: string; username: string }) => {
		return axiosInstance.post<{ message: string }>("/users/reset-password-app", {
			new_password: newPassword,
			username,
		});
	},
	verifyEmail: (username: string) => axiosInstance.post<void>("/otp/generate", { username }),
	resendOTP: (username: string) => axiosInstance.post<void>("/otp/generate", { username }),
	verifyOTP: ({ username, sub, code }: { username: string; sub: string; code: string }) => {
		const chosenDeptId = localStorageService.getChosenDepartmentID();
		const url = chosenDeptId ? `/otp/verify?department_id=${chosenDeptId}` : "/otp/verify";
		return axiosInstance.post<Token>(url, { username, sub, code });
	},
	// Password-less login: verify an OTP sent via /otp/generate. Unlike verifyOTP (Apple SSO), sends no `sub`.
	// `username` is also passed as a query param so the backend rate limiter can key per-user.
	verifyOtpLogin: ({ username, code }: { username: string; code: string }) => {
		const params = new URLSearchParams({ username });
		const chosenDeptId = localStorageService.getChosenDepartmentID();
		if (chosenDeptId) {
			params.set("department_id", chosenDeptId);
		}
		return axiosInstance.post<Token>(`/otp/login-verify?${params.toString()}`, { username, code });
	},
	createAccountRequest: (body: { name: string; facility: string; email: string; phone: string; notes: string }) => {
		return axiosInstance.post<{ message: string }>("/create-account-request", body);
	},
	pickOpenShift: ({
		acceptedStartTime,
		acceptedEndTime,
		overrideNurseId,
		departmentId,
		openShiftId,
		partial,
		targetShiftId,
	}: {
		acceptedStartTime?: DateTime;
		acceptedEndTime?: DateTime;
		overrideNurseId?: number;
		openShiftId: string;
		departmentId: string;
		partial?: boolean;
		targetShiftId?: string;
	}) => {
		return axiosInstance.post(`/staffing/pick-shift/${openShiftId}?department_id=${departmentId}`, {
			accepted_start_time: acceptedStartTime,
			accepted_end_time: acceptedEndTime,
			override_nurse_id: overrideNurseId,
			partial: partial,
			target_shift_id: targetShiftId,
		});
	},
	updateUserLanguage: (language: string) => {
		return axiosInstance.put("/users/language", { language });
	},
	updateNotificationsStatus: (status: string) => {
		return axiosInstance.post<{ status: string }>("/users/notifications-status", { status });
	},
	getUserSettings: (departmentId: string) => axiosInstance.get<UserSettings>(`/user-settings/${departmentId}`),
	updateUserSettings: (departmentId: string, settings: UpdateUserSettingsRequest) =>
		axiosInstance.put<UserSettings>(`/user-settings/${departmentId}`, settings),
	/**
	 * Resets the given (snake_case) columns to the department default. `includePrintoutPresets` also
	 * replaces the user's saved printouts with the department's and puts them back on Printout 1 —
	 * left out of the body when absent, so existing callers send exactly what they did.
	 */
	restoreUserSettings: (departmentId: string, fields: string[], options?: { includePrintoutPresets: boolean }) =>
		axiosInstance.put<UserSettings>(
			`/user-settings/${departmentId}/restore`,
			options ? { fields, includePrintoutPresets: options.includePrintoutPresets } : { fields },
		),
	// Saved Detailed-printout presets (Printout 2+). Every call answers with the full preset state.
	getPrintoutPresets: (departmentId: string) => axiosInstance.get<PrintoutPresetsResponse>(`/user-settings/${departmentId}/printout-presets`),
	createPrintoutPreset: (departmentId: string, body: { name: string; settings: PresetSettings }) =>
		axiosInstance.post<PrintoutPresetsResponse>(`/user-settings/${departmentId}/printout-presets`, body),
	updatePrintoutPresetSettings: (departmentId: string, presetId: string, settings: PresetSettings) =>
		axiosInstance.put<PrintoutPresetsResponse>(`/user-settings/${departmentId}/printout-presets/${presetId}/settings`, { settings }),
	renamePrintoutPreset: (departmentId: string, presetId: string, name: string) =>
		axiosInstance.put<PrintoutPresetsResponse>(`/user-settings/${departmentId}/printout-presets/${presetId}/name`, { name }),
	deletePrintoutPreset: (departmentId: string, presetId: string) =>
		axiosInstance.delete<PrintoutPresetsResponse>(`/user-settings/${departmentId}/printout-presets/${presetId}`),
	/** How many people in the department have saved printouts of their own (support only). */
	getPrintoutPresetsDefaultImpact: (departmentId: string) =>
		axiosInstance.get<{ usersWithOwnPresets: number }>(`/user-settings/${departmentId}/printout-presets/default-impact`),
	/** `null` switches back to Printout 1. */
	setActivePrintoutPreset: (departmentId: string, presetId: string | null) =>
		axiosInstance.put<PrintoutPresetsResponse>(`/user-settings/${departmentId}/printout-presets/active`, { presetId }),
	logProductEvent: (event: { eventType: string; action: string; departmentId: string; eventParams?: Record<string, unknown> }) =>
		axiosInstance.post("/events", event),
	/** Stores an example printout image for the AI configurator and returns the S3 key to reference it by. */
	uploadPrintoutExampleFile: (departmentId: string, file: File) => {
		const formData = new FormData();
		formData.append("file", file);
		return axiosInstance.post<{ fileKey: string }>("/printout/example-file", formData, {
			params: { department_id: departmentId },
			headers: { "Content-Type": "multipart/form-data" },
		});
	},
	/** Translates a free-text request (and/or an uploaded example image) into Detailed printout settings. */
	configurePrintoutSettingsWithAi: (body: {
		departmentId: string;
		prompt: string | null;
		fileKey: string | null;
		currentSettings: AiPrintoutSettings;
		jobManagementEnabled: boolean;
	}) => axiosInstance.post<{ settings: AiPrintoutSettings; changed: Partial<AiPrintoutSettings>; summary: string | null }>("/printout/settings/ai", body),
	getUserShifts: () => axiosInstance.get<Record<string, NurseInShift[]>>("/shifts"),
	predictCost: (body: CostPredictionRequest) => axiosInstance.post<CostPredictionResponse>("/shifts/predict-cost", body),
	predictCostBatch: (body: BatchCostPredictionRequest) => axiosInstance.post<BatchCostPredictionResponse>("/shifts/predict-cost-batch", body),
	getDraftDayStats: (body: DraftDayStatsRequest) => axiosInstance.post<DraftDayStatsResponse>("/shifts/draft-day-stats", body),
	getShiftWageBreakdown: (shiftId: string, departmentId: string, isActual?: boolean) =>
		axiosInstance.get<WageBreakdownResult>(`/shifts/${shiftId}/wage-breakdown`, { params: { department_id: departmentId, is_actual: isActual ?? false } }),
	// Support-only: find, preview and merge two duplicate staff records. `primary` is the record kept.
	searchMergeCandidates: (params: { departmentId: string; query: string; excludeNurseId: number; limit: number }) =>
		axiosInstance.get<MergeCandidatePage>("/nurse-merge/candidates", {
			params: { department_id: params.departmentId, q: params.query, exclude_nurse_id: params.excludeNurseId, limit: params.limit },
		}),
	getNurseMergePreview: (primaryNurseId: number, duplicateNurseId: number) =>
		axiosInstance.get<NurseMergePreviewWire>("/nurse-merge/preview", { params: { primary_nurse_id: primaryNurseId, duplicate_nurse_id: duplicateNurseId } }),
	mergeNurses: (body: NurseMergeRequest) => axiosInstance.post<NurseMergeResult>("/nurse-merge", body),
	// Support-only: pairs of records in the department's network that look like the same person, and marking a pair as two people.
	getSuspectedDuplicates: (params: { departmentId: string; query: string | null; limit: number; offset: number }) =>
		axiosInstance.get<SuspectedDuplicatePage>("/nurse-merge/suspected", {
			params: { department_id: params.departmentId, q: params.query ?? undefined, limit: params.limit, offset: params.offset },
		}),
	dismissSuspectedDuplicate: (body: DismissSuspectedDuplicateRequest) => axiosInstance.post<{ message: string }>("/nurse-merge/suspected/dismiss", body),
	// DAT-221: support-only raw HRIS punch events behind an actual shift (ordered by eventTime asc).
	getActualShiftPunches: (shiftId: string) => axiosInstance.get<ShiftPunchEvent[]>(`/shifts/actual/${shiftId}/punches`),
	// Every sync decision recorded about ONE actual shift — what the log opens on when support jumps
	// there from a shift's raw punches.
	getShiftChangeLog: (departmentId: string, shiftId: string) =>
		axiosInstance.get<NurseShiftLineageEntry[]>(`/employee-changelog/departments/${departmentId}/shift-lineage/shifts/${shiftId}`),
	// A short-lived presigned link to the RAW source file a sync run read. Support-only at the route: the
	// file is network-wide, so it must never be reachable from a department-scoped surface.
	getSyncRunFileUrl: (syncRunId: string) => axiosInstance.get<{ url: string }>(`/sync-lineage/runs/${syncRunId}/file-url`),
	getDayExpenses: (departmentId: string, date: string, isActual: boolean) =>
		axiosInstance.get<DayExpensesResponse>("/shifts/day-expenses", { params: { department_id: departmentId, date, is_actual: isActual } }),
	getPtoCosts: (departmentId: string, date?: string, nurseIds?: number[], startDate?: string, endDate?: string) =>
		axiosInstance.post<NurseCostsResponse>("/shifts/pto-costs", { departmentId, date, nurseIds, startDate, endDate }),
	getOpenShifts: (departmentId?: string) =>
		axiosInstance.get<Request[]>("/staffing/open-shifts", {
			params: departmentId ? { department_id: departmentId } : {},
		}),
	getIncentiveConfigurations: (departmentId: string) =>
		axiosInstance.get<IncentiveConfiguration[]>("/incentive-configuration", {
			params: { departmentId },
		}),
	createIncentiveConfiguration: (body: IncentiveConfigurationWrite) => axiosInstance.post<IncentiveConfiguration>("/incentive-configuration", body),
	updateIncentiveConfiguration: (id: string, body: IncentiveConfigurationWrite) =>
		axiosInstance.put<IncentiveConfiguration>(`/incentive-configuration/${id}`, body),
	deleteIncentiveConfiguration: (id: string) => axiosInstance.delete<void>(`/incentive-configuration/${id}`),
	getDepartmentData: (departmentId: string, wantDate: string, timezone: string) =>
		axiosInstance.get<DepartmentDataResponse>("/departments/department_data", {
			params: { department_id: departmentId, want_date: wantDate, timezone },
		}),
	getNurseSchedule: (departmentId: string, nurseId: number, startDate: string, endDate: string) =>
		axiosInstance.get<NurseScheduleResponse>("/departments/nurse_schedule", {
			params: { department_id: departmentId, nurse_id: nurseId, start_date: startDate, end_date: endDate },
		}),
	getDepartmentWageData: (departmentId: string, wantDate: string) =>
		axiosInstance.get<DepartmentWageDataResponse>("/departments/department_wage_data", {
			params: { department_id: departmentId, want_date: wantDate },
		}),
	getNurseOvertime: (departmentId: string, startDate: string, endDate: string, timezone: string, isActual: boolean) =>
		axiosInstance.get<NurseOvertimeMap>("/departments/nurse-overtime", {
			params: { department_id: departmentId, start_date: startDate, end_date: endDate, timezone, is_actual: isActual },
		}),
	getNurses: (departmentId?: string, activeOnDate?: string) =>
		axiosInstance.get<Nurse[]>("/team/nurses", {
			params: {
				...(departmentId && { department_id: departmentId }),
				...(activeOnDate && { active_on_date: activeOnDate }),
			},
		}),
	createReplaceShiftRequest: (
		departmentId: string,
		body: {
			day_number: number;
			shift_type: string;
			roles: string[];
			created_by: string;
			shift_start: string;
			shift_end: string;
			replacement_nurse_id: number;
			location?: string;
			replacement_shift_id?: string;
			charge_only: boolean;
			bonus?: number;
			job_id?: string;
		},
	) =>
		axiosInstance.post("/staffing/replace-shift", body, {
			params: { department_id: departmentId },
		}),
	createAdminSwapShiftRequest: (departmentId: string, body: { originShiftId: string; targetShiftId: string }) =>
		axiosInstance.post<{ request_id: string; shouldRefresh?: boolean }>("/staffing/swap-shift", body, {
			params: { department_id: departmentId },
		}),
	createSwapBetweenRequest: (departmentId: string, body: { originShiftId: string; targetShiftId: string }) =>
		axiosInstance.post<{ request_id: string; shouldRefresh?: boolean }>("/requests/swap-shift", body, {
			params: { department_id: departmentId },
		}),
	getSwapOptions: (departmentId: string, start_time?: DateTime, end_time?: DateTime) =>
		axiosInstance.get<Record<string, NurseInShift[]>>(`/shifts/${departmentId}`, {
			params: {
				start_time: start_time?.toFormat("yyyy-MM-dd"),
				end_time: end_time?.toFormat("yyyy-MM-dd"),
			},
		}),
	cancelReplaceShiftRequest: (requestId: string, departmentId: string) =>
		axiosInstance.delete(`/staffing/replace-shift/${requestId}`, {
			params: { department_id: departmentId },
		}),
	cancelPickedShifts: (departmentId: string, body: { nurse_id: number; request_ids: string[] }) =>
		axiosInstance.delete("/staffing/cancel-picked-shifts", {
			params: { department_id: departmentId },
			data: body,
		}),
	pickMergedShifts: (
		departmentId: string,
		body: {
			shift_ids: string[];
			accepted_start_time?: DateTime;
			accepted_end_time?: DateTime;
		},
	) =>
		axiosInstance.post("/staffing/pick-merged-shifts", body, {
			params: { department_id: departmentId },
		}),
	shiftPickupFutureNotifications: (requestId: string, departmentId: string) =>
		axiosInstance.post(`/requests/${requestId}/future-days`, null, { params: { department_id: departmentId } }),
	approveAndSwapRequest: (requestId: string, departmentId: string) =>
		axiosInstance.post(`/requests/${requestId}/approve-and-swap?department_id=${departmentId}`),
	declineSwapRequest: (requestId: string, departmentId: string) => axiosInstance.post(`/requests/${requestId}/decline-swap?department_id=${departmentId}`),
	getCommonShiftHours: (departmentId: string, roles?: string[], jobs?: string[]) =>
		axiosInstance.get<Array<{ start_time: string; end_time: string; ratio: number }>>(`/management_center/departments/${departmentId}/common-shift-hours`, {
			params: { roles, jobs },
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getManagementCenterCallIns: ({
		departmentId,
		startDate,
		endDate,
		grouping,
		roles,
		jobs,
		shiftHours,
		excludeHolidays,
	}: {
		departmentId: string;
		startDate: DateTime;
		endDate?: DateTime;
		grouping?: boolean;
		roles?: string[];
		jobs?: string[];
		shiftHours?: string[];
		excludeHolidays?: boolean;
	}) =>
		axiosInstance.get<ManagementCenterStringToDatesAndValueAggregation>(`/management_center/departments/${departmentId}/call-ins`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				grouping,
				...(endDate
					? {
							end_date: endDate.toFormat("yyyy-MM-dd"),
						}
					: {}),
				...(roles ? { roles } : {}),
				...(jobs ? { jobs } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(excludeHolidays ? { exclude_holidays: true } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getManagementCenterShiftPickUps: ({
		departmentId,
		startDate,
		endDate,
		grouping,
		roles,
		jobs,
		groupBy,
		shiftHours,
		excludeHolidays,
		excludeAgency,
	}: {
		departmentId: string;
		startDate: DateTime;
		endDate?: DateTime;
		grouping?: boolean;
		roles?: string[];
		jobs?: string[];
		groupBy?: "role" | "job";
		shiftHours?: string[];
		excludeHolidays?: boolean;
		excludeAgency?: boolean;
	}) =>
		axiosInstance.get<ManagementCenterStringToDatesAndValueAggregation>(`/management_center/departments/${departmentId}/shift-pickups`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				grouping,
				...(endDate
					? {
							end_date: endDate.toFormat("yyyy-MM-dd"),
						}
					: {}),
				...(roles ? { roles } : {}),
				...(jobs ? { jobs } : {}),
				...(groupBy ? { group_by: groupBy } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(excludeHolidays ? { exclude_holidays: true } : {}),
				...(excludeAgency === false ? { exclude_agency: false } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getManagementCenterStaffMix: ({
		departmentId,
		isActual,
		startDate,
		endDate,
		grouping,
		roles,
		jobs,
		laborCost,
		shiftHours,
		excludeHolidays,
		hideWage,
		includeLateScheduled,
		otMode,
		estimateMissingWage,
	}: {
		departmentId: string;
		isActual: boolean;
		startDate: DateTime;
		endDate?: DateTime;
		grouping?: boolean;
		roles?: string[];
		jobs?: string[];
		laborCost?: boolean;
		shiftHours?: string[];
		excludeHolidays?: boolean;
		hideWage?: boolean;
		includeLateScheduled?: boolean;
		otMode?: OtMode;
		estimateMissingWage: boolean;
	}) =>
		axiosInstance.get<ManagementCenterStaffMixResponse>(`/management_center/departments/${departmentId}/staff-mix`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				is_actual: isActual,
				grouping,
				estimate_missing_wage: estimateMissingWage,
				...(endDate
					? {
							end_date: endDate.toFormat("yyyy-MM-dd"),
						}
					: {}),
				...(roles ? { roles } : {}),
				...(jobs ? { jobs } : {}),
				...(laborCost ? { labor_cost: true } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(excludeHolidays ? { exclude_holidays: true } : {}),
				...(hideWage ? { hide_wage: true } : {}),
				...(includeLateScheduled ? { include_late_scheduled: true } : {}),
				...(otMode ? { ot_mode: otMode } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getManagementCenterHppdStats: ({
		departmentId,
		startDate,
		endDate,
		grouping,
		roles,
		jobs,
		shiftHours,
		excludeHolidays,
		includeLateScheduled,
		wantedMode,
		otMode,
	}: {
		departmentId: string;
		startDate: DateTime;
		endDate?: DateTime;
		grouping?: boolean;
		roles?: string[];
		jobs?: string[];
		shiftHours?: string[];
		excludeHolidays?: boolean;
		includeLateScheduled?: boolean;
		wantedMode?: StatsMode;
		otMode?: OtMode;
	}) =>
		axiosInstance.get<ManagementCenterHPPDResponse>(`/management_center/departments/${departmentId}/hppd-stats`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				grouping,
				...(endDate
					? {
							end_date: endDate.toFormat("yyyy-MM-dd"),
						}
					: {}),
				...(roles ? { roles } : {}),
				...(jobs ? { jobs } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(excludeHolidays ? { exclude_holidays: true } : {}),
				...(includeLateScheduled ? { include_late_scheduled: true } : {}),
				...(wantedMode ? { wanted_mode: wantedMode } : {}),
				...(otMode ? { ot_mode: otMode } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getManagementCenterHoursStats: ({
		departmentId,
		startDate,
		endDate,
		grouping,
		roles,
		jobs,
		shiftHours,
		includeLateScheduled,
		wantedMode,
		otMode,
	}: {
		departmentId: string;
		startDate: DateTime;
		endDate?: DateTime;
		grouping?: boolean;
		roles?: string[];
		jobs?: string[];
		shiftHours?: string[];
		includeLateScheduled?: boolean;
		wantedMode?: StatsMode;
		otMode?: OtMode;
	}) =>
		axiosInstance.get<ManagementCenterHoursResponse>(`/management_center/departments/${departmentId}/hours-stats`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				grouping,
				...(endDate
					? {
							end_date: endDate.toFormat("yyyy-MM-dd"),
						}
					: {}),
				...(roles ? { roles } : {}),
				...(jobs ? { jobs } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(includeLateScheduled ? { include_late_scheduled: true } : {}),
				...(wantedMode ? { wanted_mode: wantedMode } : {}),
				...(otMode ? { ot_mode: otMode } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getManagementCenterPpdStats: ({
		departmentId,
		startDate,
		endDate,
		grouping,
		roles,
		jobs,
		laborCost,
		shiftHours,
		excludeHolidays,
		hideWage,
		includeLateScheduled,
		wantedMode,
		otMode,
		estimateMissingWage,
	}: {
		departmentId: string;
		startDate: DateTime;
		endDate?: DateTime;
		grouping?: boolean;
		roles?: string[];
		jobs?: string[];
		laborCost?: boolean;
		shiftHours?: string[];
		excludeHolidays?: boolean;
		hideWage?: boolean;
		includeLateScheduled?: boolean;
		wantedMode?: StatsMode;
		otMode?: OtMode;
		estimateMissingWage: boolean;
	}) =>
		axiosInstance.get<ManagementCenterPPDResponse>(`/management_center/departments/${departmentId}/ppd-stats`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				grouping,
				estimate_missing_wage: estimateMissingWage,
				...(endDate
					? {
							end_date: endDate.toFormat("yyyy-MM-dd"),
						}
					: {}),
				...(roles ? { roles } : {}),
				...(jobs ? { jobs } : {}),
				...(laborCost ? { labor_cost: true } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(excludeHolidays ? { exclude_holidays: true } : {}),
				...(hideWage ? { hide_wage: true } : {}),
				...(includeLateScheduled ? { include_late_scheduled: true } : {}),
				...(wantedMode ? { wanted_mode: wantedMode } : {}),
				...(otMode ? { ot_mode: otMode } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getDailyStaffBreakdown: ({
		departmentId,
		targetDate,
		roles,
		jobs,
		laborCost,
		shiftHours,
		hideWage,
		includeProblems,
		includeLateScheduled,
		estimateMissingWage,
	}: {
		departmentId: string;
		targetDate: string;
		roles?: string[];
		jobs?: string[];
		shiftHours?: string[];
		laborCost?: boolean;
		hideWage?: boolean;
		includeProblems?: boolean;
		includeLateScheduled?: boolean;
		estimateMissingWage: boolean;
	}) =>
		axiosInstance.get<DailyStaffBreakdownResponse>(`/management_center/departments/${departmentId}/daily-staff-breakdown`, {
			params: {
				target_date: targetDate,
				estimate_missing_wage: estimateMissingWage,
				...(roles ? { roles } : {}),
				...(jobs ? { jobs } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(laborCost ? { labor_cost: true } : {}),
				...(hideWage ? { hide_wage: true } : {}),
				...(includeProblems ? { include_problems: true } : {}),
				...(includeLateScheduled ? { include_late_scheduled: true } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getCostDeepDive: ({
		departmentId,
		startDate,
		endDate,
		grouping,
		isActual,
		roles,
		jobs,
		laborCost,
		shiftHours,
		excludeHolidays,
		hideWage,
		includeLateScheduled,
		perCensus,
		forceWeekly,
		otMode,
		estimateMissingWage,
	}: {
		departmentId: string;
		startDate: DateTime;
		endDate?: DateTime;
		grouping?: boolean;
		isActual: boolean;
		roles?: string[];
		jobs?: string[];
		laborCost?: boolean;
		shiftHours?: string[];
		excludeHolidays?: boolean;
		hideWage?: boolean;
		includeLateScheduled?: boolean;
		perCensus?: boolean;
		forceWeekly?: boolean;
		otMode?: OtMode;
		estimateMissingWage: boolean;
	}) =>
		axiosInstance.get<CostDeepDiveResponse>(`/management_center/departments/${departmentId}/cost-deep-dive`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				grouping,
				is_actual: isActual,
				estimate_missing_wage: estimateMissingWage,
				...(endDate
					? {
							end_date: endDate.toFormat("yyyy-MM-dd"),
						}
					: {}),
				...(roles ? { roles } : {}),
				...(jobs ? { jobs } : {}),
				...(laborCost ? { labor_cost: true } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(excludeHolidays ? { exclude_holidays: true } : {}),
				...(hideWage ? { hide_wage: true } : {}),
				...(includeLateScheduled ? { include_late_scheduled: true } : {}),
				...(perCensus ? { per_census: true } : {}),
				...(forceWeekly ? { force_weekly: true } : {}),
				...(otMode ? { ot_mode: otMode } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getCostVariance: ({
		departmentId,
		startDate,
		endDate,
		grouping,
		roles,
		jobs,
		laborCost,
		shiftHours,
		excludeHolidays,
		hideWage,
		includeLateScheduled,
		otMode,
		estimateMissingWage,
	}: {
		departmentId: string;
		startDate: DateTime;
		endDate?: DateTime;
		grouping?: boolean;
		roles?: string[];
		jobs?: string[];
		laborCost?: boolean;
		shiftHours?: string[];
		excludeHolidays?: boolean;
		hideWage?: boolean;
		includeLateScheduled?: boolean;
		otMode?: OtMode;
		estimateMissingWage: boolean;
	}) =>
		axiosInstance.get<CostVarianceResponse>(`/management_center/departments/${departmentId}/cost-variance`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				grouping,
				estimate_missing_wage: estimateMissingWage,
				...(endDate
					? {
							end_date: endDate.toFormat("yyyy-MM-dd"),
						}
					: {}),
				...(roles ? { roles } : {}),
				...(jobs ? { jobs } : {}),
				...(laborCost ? { labor_cost: true } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(excludeHolidays ? { exclude_holidays: true } : {}),
				...(hideWage ? { hide_wage: true } : {}),
				...(includeLateScheduled ? { include_late_scheduled: true } : {}),
				...(otMode ? { ot_mode: otMode } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getDailyCostBreakdown: ({
		departmentId,
		targetDate,
		endDate,
		isActual,
		roles,
		jobs,
		laborCost,
		shiftHours,
		hideWage,
		includeLateScheduled,
		otMode,
		estimateMissingWage,
	}: {
		departmentId: string;
		targetDate: string;
		endDate?: string;
		isActual: boolean;
		roles?: string[];
		jobs?: string[];
		laborCost?: boolean;
		shiftHours?: string[];
		hideWage?: boolean;
		includeLateScheduled?: boolean;
		otMode?: OtMode;
		estimateMissingWage: boolean;
	}) =>
		axiosInstance.get<DailyCostBreakdownResponse>(`/management_center/departments/${departmentId}/daily-cost-breakdown`, {
			params: {
				target_date: targetDate,
				is_actual: isActual,
				estimate_missing_wage: estimateMissingWage,
				...(endDate ? { end_date: endDate } : {}),
				...(roles ? { roles } : {}),
				...(jobs ? { jobs } : {}),
				...(laborCost ? { labor_cost: true } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(hideWage ? { hide_wage: true } : {}),
				...(includeLateScheduled ? { include_late_scheduled: true } : {}),
				...(otMode ? { ot_mode: otMode } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getHoursSummary: ({
		departmentId,
		startDate,
		endDate,
		isActual,
		roles,
		jobs,
		shiftHours,
		excludeHolidays,
		includeEmployeeDetails,
		includeLateScheduled,
		otMode,
	}: {
		departmentId: string;
		startDate: DateTime;
		endDate?: DateTime;
		isActual: boolean;
		roles?: string[];
		jobs?: string[];
		shiftHours?: string[];
		excludeHolidays?: boolean;
		includeEmployeeDetails?: boolean;
		includeLateScheduled?: boolean;
		otMode?: OtMode;
	}) =>
		axiosInstance.get<HoursSummaryResponse>(`/management_center/departments/${departmentId}/hours-summary`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				is_actual: isActual,
				...(endDate
					? {
							end_date: endDate.toFormat("yyyy-MM-dd"),
						}
					: {}),
				...(roles ? { roles } : {}),
				...(jobs ? { jobs } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(excludeHolidays ? { exclude_holidays: true } : {}),
				...(includeEmployeeDetails !== undefined ? { include_employee_details: includeEmployeeDetails } : {}),
				...(includeLateScheduled ? { include_late_scheduled: true } : {}),
				...(otMode ? { ot_mode: otMode } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getDepartmentAlerts: ({
		departmentId,
		startDate,
		endDate,
		isActual,
		roles,
		shiftHours,
	}: {
		departmentId: string;
		startDate: DateTime;
		endDate?: DateTime;
		isActual: boolean;
		roles?: string[];
		shiftHours?: string[];
	}) =>
		axiosInstance.get<DepartmentAlertsResponse>(`/department-alerts/departments/${departmentId}/alerts`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				is_actual: isActual,
				...(endDate ? { end_date: endDate.toFormat("yyyy-MM-dd") } : {}),
				...(roles ? { roles } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getManagementCenterMultiPpdComparison: ({ startDate, endDate }: { startDate: DateTime; endDate?: DateTime }) =>
		axiosInstance.get<ManagementCenterMultiDepartmentResponse>("/management_center/multi/ppd-comparison", {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				...(endDate ? { end_date: endDate.toFormat("yyyy-MM-dd") } : {}),
			},
		}),
	getManagementCenterMultiHppdComparison: ({ startDate, endDate }: { startDate: DateTime; endDate?: DateTime }) =>
		axiosInstance.get<ManagementCenterMultiDepartmentResponse>("/management_center/multi/hppd-comparison", {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				...(endDate ? { end_date: endDate.toFormat("yyyy-MM-dd") } : {}),
			},
		}),
	getManagementCenterMultiStaffSatisfaction: ({ startDate, endDate }: { startDate: DateTime; endDate?: DateTime }) =>
		axiosInstance.get<ManagementCenterMultiDepartmentResponse>("/management_center/multi/staff-satisfaction", {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				...(endDate ? { end_date: endDate.toFormat("yyyy-MM-dd") } : {}),
			},
		}),
	getManagementCenterMultiShiftFillRate: ({ startDate, endDate }: { startDate: DateTime; endDate?: DateTime }) =>
		axiosInstance.get<ManagementCenterMultiDepartmentResponse>("/management_center/multi/shift-fill-rate", {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				...(endDate ? { end_date: endDate.toFormat("yyyy-MM-dd") } : {}),
			},
		}),
	getManagementCenterMultiOtUse: ({ startDate, endDate }: { startDate: DateTime; endDate?: DateTime }) =>
		axiosInstance.get<ManagementCenterMultiDepartmentResponse>("/management_center/multi/ot-use", {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				...(endDate ? { end_date: endDate.toFormat("yyyy-MM-dd") } : {}),
			},
		}),
	getManagementCenterMultiAgencyUse: ({ startDate, endDate }: { startDate: DateTime; endDate?: DateTime }) =>
		axiosInstance.get<ManagementCenterMultiDepartmentResponse>("/management_center/multi/agency-use", {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				...(endDate ? { end_date: endDate.toFormat("yyyy-MM-dd") } : {}),
			},
		}),
	getManagementCenterMultiWeekActiveUsers: ({ startDate, endDate }: { startDate: DateTime; endDate?: DateTime }) =>
		axiosInstance.get<ManagementCenterMultiDepartmentResponse>("/management_center/multi/week-active-users", {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				...(endDate ? { end_date: endDate.toFormat("yyyy-MM-dd") } : {}),
			},
		}),
	// ---- Multi-Facility Management Center (Stage 1 real endpoints; see context/arch/reporting/management-center.md) ----
	getMultiFacilityPpdStats: (args: MultiFacilityWageArgs) =>
		axiosInstance.post<MultiFacilityStatsResponse>("/management_center/multi/ppd-stats", toMultiFacilityBody(args)),
	getMultiFacilityHppdStats: (args: MultiFacilityStatsArgs) =>
		axiosInstance.post<MultiFacilityStatsResponse>("/management_center/multi/hppd-stats", toMultiFacilityBody(args)),
	getMultiFacilityCostStats: (args: MultiFacilityWageArgs) =>
		axiosInstance.post<MultiFacilityCostStatsResponse>("/management_center/multi/cost-stats", toMultiFacilityBody(args)),
	getMultiFacilityShiftPickups: (args: MultiFacilityPickupsArgs) =>
		axiosInstance.post<MultiFacilityStatsResponse>("/management_center/multi/shift-pickups", toMultiFacilityBody(args)),
	getMultiFacilityDisciplinarySummary: (args: MultiFacilityBaseArgs) =>
		axiosInstance.post<MultiFacilityDisciplinarySummaryResponse>("/management_center/multi/disciplinary-summary", toMultiFacilityBody(args)),
	getMultiFacilityKpiSummary: (args: MultiFacilityWageArgs) =>
		axiosInstance.post<MultiFacilityKpiSummaryResponse>("/management_center/multi/kpi-summary", toMultiFacilityBody(args)),
	getMultiFacilityPerformanceOverTime: (args: MultiFacilityWageArgs) =>
		axiosInstance.post<MultiFacilityPerformanceOverTimeResponse>("/management_center/multi/performance-over-time", toMultiFacilityBody(args)),
	// Network-scoped ops-backoffice metrics grid for the Management Center multi page. Same POST
	// body contract as the other multi endpoints; returns the narrowed grid rows (identity + metrics).
	getManagementCenterMultiGrid: (args: MultiFacilityBaseArgs) =>
		axiosInstance.post<MultiFacilityGridResponse>("/management_center/multi/backoffice-grid", toMultiFacilityBody(args)),
	downloadManagementCenterMultiGraph: ({ graphName, startDate, endDate }: { graphName: string; startDate: DateTime; endDate?: DateTime }) =>
		axiosInstance.get("/management_center/multi/download", {
			params: {
				graph_name: graphName,
				start_date: startDate.toFormat("yyyy-MM-dd"),
				...(endDate ? { end_date: endDate.toFormat("yyyy-MM-dd") } : {}),
			},
			responseType: "arraybuffer",
		}),
	getPushReportSchedule: ({ departmentId, reportType }: { departmentId: string; reportType: PushReportType }) =>
		axiosInstance.get<PushReportSchedule | null>(`/management_center/departments/${departmentId}/push-report-schedule`, {
			params: { report_type: reportType },
		}),
	putPushReportSchedule: ({
		departmentId,
		reportType,
		weekdays,
		columns,
	}: {
		departmentId: string;
		reportType: PushReportType;
		weekdays: number[];
		columns: PushReportColumn[] | null;
	}) =>
		axiosInstance.put<PushReportSchedule>(
			`/management_center/departments/${departmentId}/push-report-schedule`,
			{ weekdays, columns },
			{ params: { report_type: reportType } },
		),
	downloadCostDeepDive: ({
		departmentId,
		startDate,
		endDate,
		isActual,
		laborCost,
		shiftHours,
		roles,
		jobs,
		excludeHolidays,
		hideWage,
		includeLateScheduled,
		unit,
		otMode,
		estimateMissingWage,
	}: {
		departmentId: string;
		startDate: DateTime;
		endDate?: DateTime;
		isActual: boolean;
		laborCost?: boolean;
		shiftHours?: string[];
		roles?: string[];
		jobs?: string[];
		excludeHolidays?: boolean;
		hideWage?: boolean;
		includeLateScheduled?: boolean;
		unit: CostDeepDiveUnit;
		otMode?: OtMode;
		estimateMissingWage: boolean;
	}) =>
		axiosInstance.get(`/management_center/departments/${departmentId}/cost-deep-dive/download`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				is_actual: isActual,
				unit,
				estimate_missing_wage: estimateMissingWage,
				...(endDate ? { end_date: endDate.toFormat("yyyy-MM-dd") } : {}),
				...(laborCost ? { labor_cost: true } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(roles ? { roles } : {}),
				...(jobs ? { jobs } : {}),
				...(excludeHolidays ? { exclude_holidays: true } : {}),
				...(hideWage ? { hide_wage: true } : {}),
				...(includeLateScheduled ? { include_late_scheduled: true } : {}),
				...(otMode ? { ot_mode: otMode } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
			responseType: "arraybuffer",
		}),
	downloadScheduleVsActual: ({
		departmentId,
		startDate,
		endDate,
		metric,
		laborCost,
		shiftHours,
		roles,
		jobs,
		excludeHolidays,
		includeBreaks,
		hideWage,
		includeLateScheduled,
		wantedMode,
		diffMode,
		otMode,
		estimateMissingWage,
	}: {
		departmentId: string;
		startDate: DateTime;
		endDate?: DateTime;
		metric: string;
		laborCost?: boolean;
		shiftHours?: string[];
		roles?: string[];
		jobs?: string[];
		excludeHolidays?: boolean;
		includeBreaks?: boolean;
		hideWage?: boolean;
		includeLateScheduled?: boolean;
		wantedMode?: StatsMode;
		diffMode?: DifferenceMode;
		otMode?: OtMode;
		estimateMissingWage: boolean;
	}) =>
		axiosInstance.get(`/management_center/departments/${departmentId}/schedule-vs-actual/download`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				metric,
				estimate_missing_wage: estimateMissingWage,
				...(laborCost ? { labor_cost: true } : {}),
				...(endDate ? { end_date: endDate.toFormat("yyyy-MM-dd") } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(roles ? { roles } : {}),
				...(jobs ? { jobs } : {}),
				...(excludeHolidays ? { exclude_holidays: true } : {}),
				...(includeBreaks === false ? { include_breaks: false } : {}),
				...(hideWage ? { hide_wage: true } : {}),
				...(includeLateScheduled ? { include_late_scheduled: true } : {}),
				...(wantedMode ? { wanted_mode: wantedMode } : {}),
				...(diffMode ? { diff_mode: diffMode } : {}),
				...(otMode ? { ot_mode: otMode } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
			responseType: "arraybuffer",
		}),
	getLeakageReport: ({
		departmentId,
		startDate,
		endDate,
		roles,
		jobs,
		employmentTypes,
		nurseId,
	}: {
		departmentId: string;
		startDate: DateTime;
		endDate: DateTime;
		roles?: string[];
		jobs?: string[];
		employmentTypes?: string[];
		nurseId?: number;
	}) =>
		axiosInstance.get<LeakageReportResponse>(`/reports/departments/${departmentId}/leakage`, {
			params: {
				start_date: startDate.toFormat("yyyy-MM-dd"),
				// The picker's end is an EXCLUSIVE week boundary (same weekday as start, e.g. Sun→Sun);
				// the backend wants the inclusive last day, so send the day before.
				end_date: endDate.minus({ days: 1 }).toFormat("yyyy-MM-dd"),
				...(roles?.length ? { roles } : {}),
				...(jobs?.length ? { jobs } : {}),
				...(employmentTypes?.length ? { employment_types: employmentTypes } : {}),
				...(nurseId != null ? { nurse_id: nurseId } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	getDisciplinaryReport: ({
		departmentId,
		startDate,
		endDate,
		roles,
		employmentTypes,
		shiftTypes,
		shiftHours,
		violationTypes,
	}: {
		departmentId: string;
		startDate?: DateTime;
		endDate?: DateTime;
		roles?: string[];
		employmentTypes?: string[];
		shiftTypes?: string[];
		shiftHours?: string[];
		violationTypes?: DisciplinaryViolationType[];
	}) =>
		axiosInstance.get<DisciplinaryReportResponse>(`/reports/departments/${departmentId}/disciplinary`, {
			params: {
				...(startDate
					? {
							start_date: startDate.toFormat("yyyy-MM-dd"),
						}
					: {}),
				...(endDate
					? {
							end_date: endDate.toFormat("yyyy-MM-dd"),
						}
					: {}),
				...(roles ? { roles } : {}),
				...(employmentTypes ? { employment_types: employmentTypes } : {}),
				...(shiftTypes ? { shift_types: shiftTypes } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(violationTypes ? { violation_types: violationTypes } : {}),
			},
			paramsSerializer: (params) => queryString.stringify(params),
		}),
	saveDisciplinaryNote: ({
		departmentId,
		shiftId,
		shiftActualId,
		violationType,
		notes,
	}: {
		departmentId: string;
		shiftId: string;
		shiftActualId: string | null;
		violationType: DisciplinaryViolationType;
		notes: string | null;
	}) =>
		axiosInstance.patch<void>(`/reports/departments/${departmentId}/disciplinary/notes`, {
			shift_id: shiftId,
			...(shiftActualId ? { shift_actual_id: shiftActualId } : {}),
			violation_type: violationType,
			notes,
		}),
	getDisciplinaryRules: (departmentId: string) => axiosInstance.get<DisciplinaryRulesResponse>(`/reports/departments/${departmentId}/disciplinary-rules`),
	saveDisciplinaryRules: (departmentId: string, rules: DisciplinaryRulesResponse) => {
		return axiosInstance.put<DisciplinaryRulesResponse>(`/reports/departments/${departmentId}/disciplinary-rules`, rules);
	},
	exportDisciplinaryReport: ({
		departmentId,
		exportFormat,
		dateSortOrder,
		nurseSort,
		startDate,
		endDate,
		roles,
		employmentTypes,
		shiftTypes,
		shiftHours,
		violationTypes,
	}: {
		departmentId: string;
		exportFormat: "pretty" | "raw" | "overview";
		dateSortOrder: "asc" | "desc";
		nurseSort?: NurseSortItem[];
		startDate?: DateTime;
		endDate?: DateTime;
		roles?: string[];
		employmentTypes?: string[];
		shiftTypes?: string[];
		shiftHours?: string[];
		violationTypes?: DisciplinaryViolationType[];
	}) =>
		axiosInstance.post<ArrayBuffer>(
			`/reports/departments/${departmentId}/disciplinary/export`,
			{
				export_format: exportFormat,
				date_sort_order: dateSortOrder,
				...(nurseSort?.length ? { nurse_sort: nurseSort } : {}),
				...(startDate ? { start_date: startDate.toFormat("yyyy-MM-dd") } : {}),
				...(endDate ? { end_date: endDate.toFormat("yyyy-MM-dd") } : {}),
				...(roles ? { roles } : {}),
				...(employmentTypes ? { employment_types: employmentTypes } : {}),
				...(shiftTypes ? { shift_types: shiftTypes } : {}),
				...(shiftHours?.length ? { shift_hours: shiftHours } : {}),
				...(violationTypes ? { violation_types: violationTypes } : {}),
			},
			{ responseType: "arraybuffer" },
		),
	submitFeedback: (data: { sentiment: string; feedback: string; screenshot: string; departmentId: string }) => axiosInstance.post("/feedback", data),
	searchPBJFacilities: (query: string) => axiosInstance.get<PBJFacilityInfo[]>(`/pbj-calculator/search-facilities?query=${encodeURIComponent(query)}&limit=10`),
	calculatePbjMetrics: (data: {
		facility_identifier: string;
		start_month: string;
		end_month: string;
		rn_wage: number;
		lpn_wage: number;
		cna_wage: number;
		ot_percentage: number;
		ot_cost_percentage: number;
		agency_cost_percentage: number;
	}) => axiosInstance.post<PbjCalculatorResponse>("/pbj-calculator/calculate", data),
	getNurseCredentials: (nurseId: number, departmentId: string) =>
		axiosInstance.get<LocationCredentialResponse[]>(`/synergy/credentials/nurse/${nurseId}`, {
			params: { department_id: departmentId },
		}),

	getLocationCredentials: (locationId: string, departmentId: string, hideExpired: boolean = true) =>
		axiosInstance.get<LocationCredentialResponse[]>("/synergy/credentials", {
			params: { location_id: locationId, department_id: departmentId, hide_expired: hideExpired },
		}),

	createNurseCredential: (request: CreateLocationCredentialRequest, departmentId: string) =>
		axiosInstance.post<LocationCredentialResponse>("/synergy/nurse-credentialed", request, {
			params: { department_id: departmentId },
		}),

	deleteNurseCredential: (credentialId: string, departmentId: string) =>
		axiosInstance.delete<{ message: string }>(`/synergy/credentials/${credentialId}`, {
			params: { department_id: departmentId },
		}),
	uploadHubSpotCredentials: (departmentId: string, file: File): Promise<AxiosResponse<HubSpotCredentialUploadResponse>> => {
		const formData = new FormData();
		formData.append("hubspot_creds_csv", file);

		return axiosInstance.post<HubSpotCredentialUploadResponse>("/synergy/credentials/upload-hubspot", formData, {
			params: { department_id: departmentId },
			headers: { "Content-Type": "multipart/form-data" },
		});
	},
	getMissingUsersCount: (departmentId: string) => axiosInstance.get<{ count: number }>(`/team/support/missing-users-count/${departmentId}`),
	createUsersForDepartment: (departmentId: string) =>
		axiosInstance.post<CreateBatchUsersForNurseResponse>(`/team/support/create-users-for-department/${departmentId}`),
	getAgencies: (departmentId?: string) =>
		axiosInstance.get<Agency[]>("/agency-wages/agencies/all", { params: departmentId ? { department_id: departmentId } : {} }),
	// One Save of the Agency Wages grid = one request. Sending a request per role let a slow write
	// race its own retry into a duplicate insert, which surfaced as a success and a failure at once.
	bulkUpsertAgencyWages: (body: AgencyWageBulkUpsertRequest) => axiosInstance.put<AgencyWageBulkUpsertResponse>("/agency-wages/bulk", body),
	publishSelfScheduling: (data: { departmentId: string; preferences: Array<{ startDate: string; endDate: string }> }) =>
		axiosInstance.post<{ success: boolean; createdCount: number; updatedCount: number; deletedCount: number }>("/self-scheduling/publish", data),
	importSelfScheduling: (departmentId: string) => axiosInstance.post<void>(`/self-scheduling/import/${departmentId}`),
	completeSelfScheduling: (departmentId: string) => axiosInstance.post<void>(`/self-scheduling/complete/${departmentId}`),

	callOffShift: (params: { departmentId: string; shiftId: string; nurseId: number; reason: string; removalMessage?: string; changeSource?: ChangesSource }) =>
		axiosInstance.post<{ success: boolean; message: string }>("/team/call-off", null, {
			params: {
				department_id: params.departmentId,
				shift_id: params.shiftId,
				nurse_id: params.nurseId,
				reason: params.reason,
				removal_message: params.removalMessage,
				change_source: params.changeSource,
			},
		}),
	getCallOffReplacementGate: (params: { departmentId: string; shiftId: string }) =>
		axiosInstance.get<CallOffReplacementGateResponse>("/team/call-off/replacement-gate", {
			params: {
				department_id: params.departmentId,
				shift_id: params.shiftId,
			},
		}),
	getImpersonatableUsers: (departmentId: string) =>
		axiosInstance.get<Array<{ username?: string | null; phone_number?: string | null; first_name?: string | null; last_name?: string | null }>>(
			"/impersonation/impersonatable-users",
			{
				params: {
					department_id: departmentId,
				},
			},
		),

	stopImpersonation: (departmentId?: string | null) =>
		axiosInstance.post<{ access_token: string; token_type: string }>(
			"/impersonation/stop",
			{},
			{
				params: departmentId ? { department_id: departmentId } : {},
			},
		),

	// Re-mint the token for a department, optionally switching view (admin/staff). The view is a
	// request only — the server re-checks the role and can 403.
	// Re-mint an impersonation token for a department and view. Used by the view switch while
	// impersonating, where /token/department would authenticate as the support user instead.
	impersonateUserInView: (userIdentifier: string, departmentId: string, view: UserView, supportToken: string) =>
		axiosInstance.post<Token>(`/impersonation/${encodeURIComponent(userIdentifier)}`, undefined, {
			params: { department_id: departmentId, view },
			// Must be the SUPPORT token: the ambient one belongs to the impersonated user, who is
			// not support, so the endpoint would refuse it.
			headers: { Authorization: `Bearer ${supportToken}` },
		}),

	getDepartmentToken: (departmentId: string, view?: UserView, refreshToken?: string) =>
		axiosInstance.post<Token>("/token/department", refreshToken ? { refresh_token: refreshToken } : undefined, {
			params: view ? { department_id: departmentId, view } : { department_id: departmentId },
		}),

	getAdminGuide: () => axiosInstance.get<DocumentData>("/docs/admin-guide"),
	getUserGuide: () => axiosInstance.get<DocumentData>("/docs/user-guide"),
	getHistoryDistribution: (departmentId: string) =>
		axiosInstance.get<Array<{ role_group: string; start_time: string; end_time: string; ratio: number; job_id?: string | null }>>(
			"/shifts/history-distribution",
			{
				params: { department_id: departmentId },
			},
		),
	/**
	 * Named rather than positional, and with no defaults: nine positional booleans/strings in a row is
	 * how a caller silently produces the wrong file, and a default here would hide a caller that
	 * forgot to pass the user's actual choice.
	 */
	getMonthlyScheduleDownload: (params: {
		departmentId: string;
		startDate: string;
		endDate: string;
		sortEntries: MonthlySortEntries;
		shiftTimeSeparation: ShiftTimeSeparation;
		shiftCellDisplay: ShiftCellDisplay;
		printGaps: boolean;
		showHireDate: boolean;
		showPtoSheet: boolean;
		recurringOnly: boolean;
		hideAgency: boolean;
		orientation: PrintoutOrientation;
		fitToPage: boolean;
		fitToWidth: number;
		fitToHeight: number;
		/** Preview-measured px widths keyed "0".."N". Sent so the .xlsx matches the approved preview. */
		columnWidths: Record<string, number>;
	}) =>
		axiosInstance.get("/downloads/monthly-schedule", {
			params: {
				department_id: params.departmentId,
				start_date: params.startDate,
				end_date: params.endDate,
				file_type: "excel",
				// A JSON object rather than a bare value: the backend validates it through the same
				// Pydantic model the settings column uses, mirroring `column_widths` below.
				//
				// `sort_by` (the ORIGINAL, deprecated single-value param) is deliberately NOT sent — it
				// still has a server-side default and is never read by the export logic; only
				// `sort_entries` (additive alongside it) actually drives the sort. See
				// `MonthlyExportSortBy`'s docstring in `backend/models/enums.py`.
				sort_entries: JSON.stringify({ sortEntries: params.sortEntries }),
				shift_time_separation: params.shiftTimeSeparation,
				shift_cell_display: params.shiftCellDisplay,
				print_gaps: params.printGaps,
				show_hire_date: params.showHireDate,
				show_pto_sheet: params.showPtoSheet,
				recurring_only: params.recurringOnly,
				hide_agency: params.hideAgency,
				orientation: params.orientation,
				fit_to_page: params.fitToPage,
				fit_to_width: params.fitToWidth,
				fit_to_height: params.fitToHeight,
				// A JSON object rather than repeated params: the backend validates it through the same
				// Pydantic model the settings column uses, so panel and download cannot disagree.
				column_widths: JSON.stringify({ columnWidths: params.columnWidths }),
			},
			responseType: "arraybuffer",
		}),
	/**
	 * `nurseIds` scopes the spreadsheet to exactly those employees — the Team page's filters and
	 * search live in the frontend, so the backend cannot re-derive the visible roster. POST because
	 * a large facility's roster exceeds what belongs in a query string.
	 */
	postTeamExport: (departmentId: string, nurseIds: number[]) =>
		axiosInstance.post("/downloads/team-export", { department_id: departmentId, nurse_ids: nurseIds }, { responseType: "arraybuffer" }),
	postScheduleTemplateExport: (payload: ScheduleTemplateExportPayload) =>
		axiosInstance.post("/downloads/schedule-template", payload, {
			responseType: "arraybuffer",
		}),
	getFacilityScheduleDownload: (params: { departmentId: string; locationName: string; startDate: string; endDate: string }) =>
		axiosInstance.get("/downloads/facility-schedule", {
			params: {
				department_id: params.departmentId,
				location_name: params.locationName,
				start_date: params.startDate,
				end_date: params.endDate,
			},
			responseType: "arraybuffer",
		}),
	getTimeOffRequests: () => axiosInstance.get<Array<TimeOffRequest>>("/time-off-requests/my-requests"),
	getDepartmentTimeOffRequests: (departmentId: string, includeAllStatuses = false, effectiveFrom?: DateTime) =>
		axiosInstance.get<Array<TimeOffRequest>>("/time-off-requests/", {
			params: {
				department_id: departmentId,
				...(includeAllStatuses && { include_all_statuses: true }),
				...(effectiveFrom && { effective_from: effectiveFrom.toISODate() }),
			},
		}),
	getAgentNurseActions: (requestId: string, departmentId: string, source: AgentSource) =>
		axiosInstance.get<Array<FlatNurseAction>>(`/shift-pickup-agent/nurse-actions/${requestId}`, {
			params: {
				department_id: departmentId,
				source,
			},
		}),
	createTimeOffRequest: ({ departmentId, body }: { departmentId: string; body: CreateTimeOffRequestBody }) =>
		axiosInstance.post<TimeOffRequest>("/time-off-requests", body, {
			params: {
				department_id: departmentId,
			},
		}),
	runNurseAction: (nurseActionId: string, departmentId: string, source: AgentSource) =>
		axiosInstance.post(
			"/shift-pickup-agent/run-nurse-action",
			{
				nurse_action_id: nurseActionId,
			},
			{
				params: {
					department_id: departmentId,
					source,
				},
			},
		),
	getDepartmentActionsMetadata: (departmentId: string, startTime?: string, endTime?: string, request_id?: string, view?: AgentReportView) =>
		axiosInstance.get<DepartmentActionsMetadataResponse>(`/shift-pickup-agent/department-actions-metadata/${departmentId}`, {
			params: {
				...(startTime && { start_time: startTime }),
				...(endTime && { end_time: endTime }),
				...(request_id && { request_id }),
				...(view && { view }),
			},
		}),
	getDepartmentAgentRequests: (departmentId: string, startTime?: DateTime, endTime?: DateTime) =>
		axiosInstance.get<string[]>(`/shift-pickup-agent/department-actions/${departmentId}`, {
			params: {
				...(startTime && { start_time: startTime.toISO() }),
				...(endTime && { end_time: endTime.toISO() }),
			},
		}),
	approveTimeOffRequest: (requestId: string, departmentId: string) =>
		axiosInstance.put<TimeOffRequest>(`/time-off-requests/${requestId}/approve`, null, {
			params: {
				department_id: departmentId,
			},
		}),
	declineTimeOffRequest: (requestId: string, departmentId: string) =>
		axiosInstance.put<TimeOffRequest>(`/time-off-requests/${requestId}/decline`, null, {
			params: {
				department_id: departmentId,
			},
		}),
	cancelTimeOffRequest: (requestId: string) => axiosInstance.delete<TimeOffRequest>(`/time-off-requests/${requestId}`),
	pauseShiftPickupAgent: (requestId: string, departmentId: string, source: AgentSource) =>
		axiosInstance.post(
			"/shift-pickup-agent/pause",
			{
				request_id: requestId,
			},
			{
				params: {
					department_id: departmentId,
					source,
				},
			},
		),
	resumeShiftPickupAgent: (requestId: string, departmentId: string, source: AgentSource) =>
		axiosInstance.post(
			"/shift-pickup-agent/resume",
			{
				request_id: requestId,
			},
			{
				params: {
					department_id: departmentId,
					source,
				},
			},
		),
	createCallOffAgent: (requestId: string, departmentId: string) =>
		axiosInstance.post(`/shift-pickup-agent/call-off-agent/${requestId}`, null, {
			params: {
				department_id: departmentId,
			},
		}),
	getNurseScoresForShifts: (requestIds: string[], source: AgentSource) =>
		axiosInstance.post<{ scores: Record<string, number> }>("/shift-pickup-agent/nurse-scores", { request_ids: requestIds }, { params: { source } }),
	getDepartmentNurseUnions: (departmentId: string) => axiosInstance.get<string[]>(`/team/nurse-unions/${departmentId}`),
	getSheetNames: (file: File) => {
		const formData = new FormData();
		formData.append("file", file);
		return axiosInstance.post<{ sheet_names: string[] }>("/load_schedule/sheet_names", formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
	},

	loadScheduleShifts: (params: {
		departmentId: string;
		file: File;
		source?: ExternalSource;
		allowDuplicateNurseMatches?: boolean;
		promptHistoryId?: string;
	}) => {
		const formData = new FormData();
		formData.append("file", params.file);
		return axiosInstance.post("/load_schedule/load_shifts", formData, {
			params: {
				department_id: params.departmentId,
				...(params.source ? { source: params.source } : {}),
				...(params.allowDuplicateNurseMatches ? { allow_duplicate_nurse_matches: true } : {}),
				...(params.promptHistoryId ? { prompt_history_id: params.promptHistoryId } : {}),
			},
			headers: { "Content-Type": "multipart/form-data" },
		});
	},

	loadScheduleRecurringShifts: (params: {
		departmentId: string;
		file: File;
		minStartDate?: string;
		allowDuplicateNurseMatches?: boolean;
		promptHistoryId?: string;
	}) => {
		const formData = new FormData();
		formData.append("file", params.file);
		return axiosInstance.post("/load_schedule/load_recurring_shifts", formData, {
			params: {
				department_id: params.departmentId,
				...(params.minStartDate ? { min_start_date: params.minStartDate } : {}),
				...(params.allowDuplicateNurseMatches ? { allow_duplicate_nurse_matches: true } : {}),
				...(params.promptHistoryId ? { prompt_history_id: params.promptHistoryId } : {}),
			},
			headers: { "Content-Type": "multipart/form-data" },
		});
	},

	resolveShiftConflicts: (params: { departmentId: string; decisions: ConflictDecision[] }) =>
		axiosInstance.post<ResolveConflictsResult>("/load_schedule/resolve_shift_conflicts", {
			department_id: params.departmentId,
			decisions: params.decisions,
		}),

	createAutoPromptJob: (params: {
		departmentId: string;
		effort: AutoPromptEffort;
		idempotencyKey: string;
		rosterFiles: File[];
		shiftsFiles: File[];
		templateFiles: File[];
	}) => {
		const formData = new FormData();
		formData.append("department_id", params.departmentId);
		formData.append("effort", params.effort);
		for (const file of params.rosterFiles) formData.append("roster_files", file);
		for (const file of params.shiftsFiles) formData.append("shifts_files", file);
		for (const file of params.templateFiles) formData.append("template_files", file);
		return axiosInstance.post<OnboardingJob>("/onboarding_tools/jobs/auto-prompt", formData, {
			headers: { "Content-Type": "multipart/form-data", "Idempotency-Key": params.idempotencyKey },
		});
	},

	createCsvTransformJob: (
		params: {
			departmentId: string;
			idempotencyKey: string;
			items: CreateCsvTransformJobItem[];
		} & (
			| { origin: "data_load"; files: File[]; sourceJobId?: never; configuration: DataLoadJobConfiguration }
			| { origin: "auto_prompt"; files?: never; sourceJobId: string; configuration?: never }
		),
	) => {
		const formData = new FormData();
		formData.append("department_id", params.departmentId);
		formData.append("origin", params.origin);
		if (params.files) {
			for (const file of params.files) formData.append("files", file);
		} else {
			formData.append("source_job_id", params.sourceJobId);
		}
		formData.append("items", JSON.stringify(params.items));
		if (params.origin === "data_load") formData.append("configuration", JSON.stringify(params.configuration));
		return axiosInstance.post<OnboardingJob>("/onboarding_tools/jobs/csv-transform", formData, {
			headers: { "Content-Type": "multipart/form-data", "Idempotency-Key": params.idempotencyKey },
		});
	},

	listOnboardingJobs: (params: { departmentId: string; active?: boolean }) =>
		axiosInstance.get<{ jobs: OnboardingJob[] }>("/onboarding_tools/jobs", {
			params: { department_id: params.departmentId, ...(params.active === undefined ? {} : { active: params.active }) },
		}),

	getOnboardingJob: (jobId: string, departmentId: string) =>
		axiosInstance.get<OnboardingJob>(`/onboarding_tools/jobs/${jobId}`, { params: { department_id: departmentId } }),

	cancelOnboardingJob: (jobId: string, departmentId: string) =>
		axiosInstance.post<OnboardingJob>(`/onboarding_tools/jobs/${jobId}/cancel`, undefined, { params: { department_id: departmentId } }),

	retryOnboardingJobItem: (jobId: string, itemId: string, departmentId: string) =>
		axiosInstance.post<OnboardingJob>(`/onboarding_tools/jobs/${jobId}/items/${itemId}/retry`, undefined, { params: { department_id: departmentId } }),

	getAutoPromptJobOutput: (jobId: string, departmentId: string) =>
		axiosInstance.get<AutoPromptReport>(`/onboarding_tools/jobs/${jobId}/output`, { params: { department_id: departmentId } }),

	getOnboardingJobItemOutput: (jobId: string, itemId: string, departmentId: string) =>
		axiosInstance.get<ArrayBuffer>(`/onboarding_tools/jobs/${jobId}/items/${itemId}/output`, {
			params: { department_id: departmentId },
			responseType: "arraybuffer",
		}),

	getOnboardingJobItemSource: (jobId: string, itemId: string, departmentId: string) =>
		axiosInstance.get<ArrayBuffer>(`/onboarding_tools/jobs/${jobId}/items/${itemId}/source`, {
			params: { department_id: departmentId },
			responseType: "arraybuffer",
		}),

	getLatestPromptHistory: (departmentId: string, fileType: PromptHistoryFileType) =>
		axiosInstance.get<{
			id: string;
			department_id: string;
			file_name: string;
			file_type: PromptHistoryFileType;
			prompt: string;
			tab_instructions: Record<string, string>;
			s3_bucket: string | null;
			s3_key: string | null;
			load: boolean;
			loaded_at: string | null;
			created_at: string;
			created_by: string | null;
		}>("/onboarding_tools/prompt_history/latest", {
			params: { department_id: departmentId, file_type: fileType },
		}),

	getPromptHistoryFileLink: (promptHistoryId: string) => axiosInstance.get<{ url: string }>(`/onboarding_tools/prompt_history/${promptHistoryId}/file`),

	calculateRecurringShifts: (departmentId: string) => {
		return axiosInstance.post("/load_schedule/calculate_recurring", null, {
			params: { department_id: departmentId },
			responseType: "arraybuffer",
		});
	},
	/**
	 * Propose one standing pattern per employee, bucketed and scored, for review (DAT-774).
	 * Read-only — nothing is written until `approveRecurringCandidates`.
	 */
	calculateRecurringCandidates: (params: { departmentId: string; settings: RecurringCalculationSettings }) => {
		return axiosInstance.post<RecurringCalculationResult>("/load_schedule/calculate_recurring_candidates", params.settings, {
			params: { department_id: params.departmentId },
		});
	},

	/** Create templates for the employees the operator kept. This is the only call that writes. */
	approveRecurringCandidates: (params: { departmentId: string; settings: RecurringCalculationSettings; approvedCandidateIds: string[] }) => {
		return axiosInstance.post<RecurringCreationResult>(
			"/load_schedule/approve_recurring_candidates",
			{ settings: params.settings, approvedCandidateIds: params.approvedCandidateIds },
			{ params: { department_id: params.departmentId } },
		);
	},

	/** Store reviewed shift hours as new per-job rows in Department Configuration. Never overwrites an existing row. */
	saveRecurringShiftHours: (params: { departmentId: string } & SaveRecurringShiftHoursRequest) => {
		return axiosInstance.post<SaveRecurringShiftHoursResult>(
			"/load_schedule/save_recurring_shift_hours",
			{ jobs: params.jobs },
			{ params: { department_id: params.departmentId } },
		);
	},

	/** One employee's loaded schedule and punches, for the review calendar. Read-only. */
	getRecurringEmployeeHistory: (params: { departmentId: string; nurseId: number; windowMonths: number }) => {
		return axiosInstance.get<RecurringEmployeeHistory>("/load_schedule/recurring_employee_history", {
			params: { department_id: params.departmentId, nurse_id: params.nurseId, window_months: params.windowMonths },
		});
	},

	getRecurringStartPreview: (params: { departmentId: string; minStartDate?: string }) => {
		return axiosInstance.get<RecurringStartPreview>("/load_schedule/recurring_start_preview", {
			params: {
				department_id: params.departmentId,
				...(params.minStartDate ? { min_start_date: params.minStartDate } : {}),
			},
		});
	},
	// The backend query param is named `end_date`, but it is really an inclusive *start* cutoff:
	// everything on or after it is deleted. Keep the wire name, expose the honest one to callers.
	cleanSchedule: (params: { departmentId: string; deleteFromDate?: string; cleanScheduleTemplate?: boolean }) => {
		return axiosInstance.post("/load_schedule/clean_schedule", null, {
			params: {
				department_id: params.departmentId,
				...(params.deleteFromDate ? { end_date: params.deleteFromDate } : {}),
				...(params.cleanScheduleTemplate ? { clean_schedule_template: params.cleanScheduleTemplate } : {}),
			},
		});
	},
	generateOnboardingReport: (departmentId: string) => {
		return axiosInstance.post("/load_schedule/generate_onboarding_report", null, {
			params: { department_id: departmentId },
			responseType: "arraybuffer",
		});
	},
	getPtoDeletedShifts: (departmentId: string, startDate: string, endDate: string) =>
		axiosInstance.get<
			Array<{
				nurseId: number;
				departmentId: string;
				startTime: string;
				endTime: string;
				location: string | null;
				shiftType: string | null;
				trainingStatus: string;
				charge: boolean;
				notes: string | null;
			}>
		>("/shifts/pto-deleted", {
			params: {
				department_id: departmentId,
				start_date: startDate,
				end_date: endDate,
			},
		}),
	loadNewNurses: (params: { departmentId: string; file: File; promptHistoryId?: string }) => {
		const formData = new FormData();
		formData.append("file", params.file);
		return axiosInstance.post("/scripts/load_new_nurses", formData, {
			params: {
				department_id: params.departmentId,
				...(params.promptHistoryId ? { prompt_history_id: params.promptHistoryId } : {}),
			},
			headers: { "Content-Type": "multipart/form-data" },
		});
	},
	addHeldRowsToDepartment: (params: { departmentId: string; items: AddToDepartmentItem[] }) =>
		axiosInstance.post<AddToDepartmentResponse>("/scripts/roster/add_to_department", { department_id: params.departmentId, items: params.items }),
	createHeldRows: (params: { departmentId: string; isUpdate: boolean; rows: Array<Record<string, HeldCellValue>> }) =>
		axiosInstance.post<CreateHeldResponse>("/scripts/roster/create_held", {
			department_id: params.departmentId,
			is_update: params.isUpdate,
			rows: params.rows,
		}),
	updateExistingNurses: (params: { departmentId: string; file: File; promptHistoryId?: string }) => {
		const formData = new FormData();
		formData.append("file", params.file);
		return axiosInstance.post("/scripts/update_existing_nurses", formData, {
			params: {
				department_id: params.departmentId,
				...(params.promptHistoryId ? { prompt_history_id: params.promptHistoryId } : {}),
			},
			headers: { "Content-Type": "multipart/form-data" },
		});
	},
	getOpsBackofficeDashboard: (
		startDate: DateTime,
		endDate: DateTime,
		departmentId?: string,
		baselineStart?: DateTime,
		baselineEnd?: DateTime,
	): Promise<AxiosResponse<OpsBackofficeResponse>> => {
		return axiosInstance.get<OpsBackofficeResponse>("/ops-backoffice/dashboard", {
			params: {
				start_date: startDate.toISODate(),
				end_date: endDate.toISODate(),
				department_id: departmentId,
				baseline_start_date: baselineStart?.toISODate(),
				baseline_end_date: baselineEnd?.toISODate(),
			},
		});
	},
	updateOpsBackofficeDepartmentInput: (departmentId: string, data: DepartmentInputUpdate): Promise<AxiosResponse<UpdateOpsBackofficeUserInputResponse>> => {
		return axiosInstance.put<UpdateOpsBackofficeUserInputResponse>(`/ops-backoffice/departments/${departmentId}/input`, data);
	},
	getOpsBackofficeIncentiveShifts: (departmentId: string): Promise<AxiosResponse<IncentiveShiftsResponse>> => {
		return axiosInstance.get<IncentiveShiftsResponse>(`/ops-backoffice/departments/${departmentId}/incentive-shifts`);
	},
	getOpsBackofficeDepartmentUsers: (departmentId: string, startDate: DateTime, endDate: DateTime): Promise<AxiosResponse<DepartmentUsersResponse>> => {
		return axiosInstance.get<DepartmentUsersResponse>(`/ops-backoffice/departments/${departmentId}/users`, {
			params: { start_date: startDate.toISODate(), end_date: endDate.toISODate() },
		});
	},
	getOpsBackofficeNotificationOptOuts: (departmentId: string): Promise<AxiosResponse<NotificationOptOutsResponse>> => {
		return axiosInstance.get<NotificationOptOutsResponse>(`/ops-backoffice/departments/${departmentId}/notification-optouts`);
	},
	getOpsBackofficeDataLoadSummary: (departmentId: string): Promise<AxiosResponse<DataLoadValidationSummariesResponse>> => {
		return axiosInstance.get<DataLoadValidationSummariesResponse>(`/ops-backoffice/departments/${departmentId}/data-load-summary`);
	},
	getOpsBackofficeSupportUsers: (): Promise<AxiosResponse<SupportUsersResponse>> => {
		return axiosInstance.get<SupportUsersResponse>("/ops-backoffice/support-users");
	},
	getOpsBackofficePresets: (): Promise<AxiosResponse<OpsPresetsResponse>> => {
		return axiosInstance.get<OpsPresetsResponse>("/ops-backoffice/presets");
	},
	createOpsBackofficePreset: (body: OpsPresetCreatePayload): Promise<AxiosResponse<OpsPreset>> => {
		return axiosInstance.post<OpsPreset>("/ops-backoffice/presets", body);
	},
	updateOpsBackofficePreset: (presetId: string, body: OpsPresetUpdatePayload): Promise<AxiosResponse<OpsPreset>> => {
		return axiosInstance.put<OpsPreset>(`/ops-backoffice/presets/${presetId}`, body);
	},
	// Records the view the user just applied, so the page reopens on it. The endpoint is named "default"
	// for historical reasons — it is the published contract; only its caller changed (automatic on apply).
	setOpsBackofficeLastUsedPreset: (presetId: string): Promise<AxiosResponse<OpsPresetsResponse>> => {
		return axiosInstance.put<OpsPresetsResponse>("/ops-backoffice/presets/default", { presetId });
	},
	deleteOpsBackofficePreset: (presetId: string): Promise<AxiosResponse<OpsPresetsResponse>> => {
		return axiosInstance.delete<OpsPresetsResponse>(`/ops-backoffice/presets/${presetId}`);
	},
	pinOpsBackofficePreset: (presetId: string): Promise<AxiosResponse<OpsPresetsResponse>> => {
		return axiosInstance.post<OpsPresetsResponse>(`/ops-backoffice/presets/${presetId}/pin`);
	},
	unpinOpsBackofficePreset: (presetId: string): Promise<AxiosResponse<OpsPresetsResponse>> => {
		return axiosInstance.delete<OpsPresetsResponse>(`/ops-backoffice/presets/${presetId}/pin`);
	},
	getOpenStatusDecisions: (departmentId: string): Promise<AxiosResponse<OpenStatusDecision[]>> => {
		return axiosInstance.get<OpenStatusDecision[]>(`/team/status-decisions/${departmentId}`);
	},
	applyStatusDecision: (decisionId: string, choice: StatusDecisionChoice): Promise<AxiosResponse<void>> => {
		return axiosInstance.post<void>("/team/status-decision", { decision_id: decisionId, choice });
	},

	// Jobs
	getJobs: () => axiosInstance.get<Job[]>("/jobs"),
	createJob: (body: { displayName: string; excludeFromPpd: boolean; deptId?: string; certRoleStrings?: string[] }) => axiosInstance.post<Job>("/jobs", body),
	getDefaultJobIds: (deptType: string) => axiosInstance.get<string[]>(`/jobs/defaults/${deptType}`),
	getCertificationDefaults: () => axiosInstance.get<CertificationJobMapping[]>("/jobs/certification-defaults-merged"),
	getDepartmentCertJobMapping: (departmentId: string) => axiosInstance.get<CertificationJobMapping[]>(`/departments/${departmentId}/certification-job-mapping`),
	getJobRecommendations: (departmentId: string) => axiosInstance.get<RoleJobRecommendation[]>(`/departments/${departmentId}/job-recommendations`),
	getDepartmentSubRoles: (departmentId: string) => axiosInstance.get<JobDepartmentSubRole[]>(`/departments/${departmentId}/sub-roles`),
	// Upsert keyed by (department, job, subRole, source). `replacesRowId`: on a key change the backend deletes that old row too.
	updateDepartmentSubRole: (departmentId: string, body: SubRoleUpsertBody) =>
		axiosInstance.put<JobDepartmentSubRole>(`/departments/${departmentId}/sub-roles`, body),
	deleteDepartmentSubRole: (departmentId: string, rowId: string) => axiosInstance.delete<void>(`/departments/${departmentId}/sub-roles/${rowId}`),
	getHrisRawEmployeesForDepartment: (departmentId: string) => axiosInstance.get<HrisRawEmployeeRow[]>(`/departments/${departmentId}/hris-raw-employees`),
	getHrisRawPtoEventTypesForDepartment: (departmentId: string) => axiosInstance.get<string[]>(`/departments/${departmentId}/hris-raw-pto-event-types`),
	getHrisPtoTypeStats: (departmentId: string) => axiosInstance.get<HrisPtoTypeStats[]>(`/departments/${departmentId}/hris-pto-type-stats`),
	// DAT-200: how many synced time-offs "Update previous" would re-map for a raw string (confirm-dialog count).
	getTimeOffRemapPreview: (departmentId: string, rawString: string) =>
		axiosInstance.get<TimeOffRemapPreview>(`/departments/${departmentId}/time-off-remap-preview`, { params: { rawString } }),
	getHrisEmployeeShiftStats: (departmentId: string) => axiosInstance.get<HrisEmployeeShiftStats[]>(`/departments/${departmentId}/hris-employee-shift-stats`),
	// DAT-834: active employees whose primary job the department doesn't enable. Support-gated like
	// every other endpoint on this router — both screens that read it live under Department
	// Configuration, which is support-only end to end, so facility admins deliberately no longer get
	// this signal at all (it replaced the Team page's `job_not_enabled` ask, which they did see).
	getJobNotEnabledEmployees: (departmentId: string) => axiosInstance.get<JobNotEnabledEmployee[]>(`/departments/${departmentId}/job-not-enabled-employees`),
	getIntegrationEmployeesSummary: (departmentId: string, startDate: DateTime, endDate: DateTime) =>
		axiosInstance.get<IntegrationEmployeesSummary>(`/departments/${departmentId}/integration-summary/employees`, {
			params: { startDate: startDate.toFormat("yyyy-MM-dd"), endDate: endDate.toFormat("yyyy-MM-dd") },
		}),
	getIntegrationTimeOffSummary: (departmentId: string, startDate: DateTime, endDate: DateTime) =>
		axiosInstance.get<IntegrationTimeOffSummary>(`/departments/${departmentId}/integration-summary/time-off`, {
			params: { startDate: startDate.toFormat("yyyy-MM-dd"), endDate: endDate.toFormat("yyyy-MM-dd") },
		}),
	downloadIntegrationEmployeesSummary: (departmentId: string, startDate: DateTime, endDate: DateTime) =>
		axiosInstance.get<ArrayBuffer>(`/departments/${departmentId}/integration-summary/employees/download`, {
			params: { startDate: startDate.toFormat("yyyy-MM-dd"), endDate: endDate.toFormat("yyyy-MM-dd") },
			responseType: "arraybuffer",
		}),
	downloadIntegrationTimeOffSummary: (departmentId: string, startDate: DateTime, endDate: DateTime) =>
		axiosInstance.get<ArrayBuffer>(`/departments/${departmentId}/integration-summary/time-off/download`, {
			params: { startDate: startDate.toFormat("yyyy-MM-dd"), endDate: endDate.toFormat("yyyy-MM-dd") },
			responseType: "arraybuffer",
		}),
	getIntegrationShiftsSummary: (departmentId: string, startDate: DateTime, endDate: DateTime) =>
		axiosInstance.get<IntegrationShiftsSummary>(`/departments/${departmentId}/integration-summary/shifts`, {
			params: { startDate: startDate.toFormat("yyyy-MM-dd"), endDate: endDate.toFormat("yyyy-MM-dd") },
		}),
	// Current-state check, deliberately not scoped by the summary's date range.
	getIntegrationShiftsRecurringPatterns: (departmentId: string) =>
		axiosInstance.get<MissingRecurringPatternsSummary>(`/departments/${departmentId}/integration-summary/shifts/recurring-patterns`),
	downloadIntegrationShiftsSummary: (departmentId: string, startDate: DateTime, endDate: DateTime) =>
		axiosInstance.get<ArrayBuffer>(`/departments/${departmentId}/integration-summary/shifts/download`, {
			params: { startDate: startDate.toFormat("yyyy-MM-dd"), endDate: endDate.toFormat("yyyy-MM-dd") },
			responseType: "arraybuffer",
		}),
	// ───── Clipboard onboarding (support-only wizard, Department Configuration → Integrations) ─────
	// Validates the login and lists the workplaces it manages. Writes NOTHING: enrolling a department
	// before a facility is chosen would go live on the next 15-minute tick with no role catalog.
	verifyClipboardCredentials: (departmentId: string, body: { email: string; password: string }) =>
		axiosInstance.post<ClipboardVerifyCredentialsResponse>(`/departments/${departmentId}/clipboard/verify-credentials`, body),
	// The one committing call: writes the credential entry, then `clipboard_facility_id`, in one confirmed step.
	createClipboardEnrollment: (departmentId: string, body: { email: string; password: string; facilityId: string }) =>
		axiosInstance.post<ClipboardEnrollmentResponse>(`/departments/${departmentId}/clipboard/enrollment`, body),
	// Read-only dry run of the syncer's own classifier over the facility's contracted worker types.
	// `facilityId` is a query param, not read off the department row, so the proposal can run pre-commit.
	getClipboardRoleMappingProposal: (departmentId: string, facilityId: string) =>
		axiosInstance.get<ClipboardRoleMappingProposalResponse>(`/departments/${departmentId}/clipboard/role-mapping-proposal`, { params: { facilityId } }),
	// Bulk apply in ONE transaction; the response re-runs the classifier so the UI can show the residual
	// unmapped count as evidence rather than assertion.
	applyClipboardRoleMapping: (departmentId: string, body: ClipboardRoleMappingApplyBody) =>
		axiosInstance.post<ClipboardRoleMappingProposalResponse>(`/departments/${departmentId}/clipboard/role-mapping`, body),
	// ───── Airtable config import (support-only, Department Configuration header) ─────
	// EVERY implementation-intake record in the base, in one read-only call, wrapped in `candidates`.
	// The picker filters that loaded list client-side, so there is no per-keystroke request and no
	// minimum query length; the endpoint's `q` is optional for exactly this.
	getAirtableConfigFacilities: () => axiosInstance.get<AirtableFacilitiesResponse>("/airtable-config/facilities"),
	// The Airtable-side search behind the same endpoint, used ONLY when the listing above came back
	// truncated: the in-memory filter cannot find a record that was never loaded. `q` is 2–100 chars.
	searchAirtableConfigFacilities: (q: string) => axiosInstance.get<AirtableFacilitiesResponse>("/airtable-config/facilities", { params: { q } }),
	// The per-field diff between one intake record and ONE department's saved config. Writes nothing;
	// the fingerprint it returns identifies the record revision the diff was computed from.
	previewAirtableConfig: (body: { departmentId: string; airtableRecordId: string }) =>
		axiosInstance.post<AirtablePreviewResponse>("/airtable-config/preview", body),
	// Re-reads Airtable immediately before the operator applies their decisions. Refuses with
	// `airtable.import.record.changed` when the record moved under them, so accept decisions can never
	// be carried over onto text nobody reviewed.
	verifyAirtableConfig: (body: { departmentId: string; airtableRecordId: string; expectedFingerprint: string }) =>
		axiosInstance.post<AirtableVerifyResponse>("/airtable-config/verify", body),
	// DAT-535: active linked employees a primary-job change would (or wouldn't) re-job — drives the confirm dialog before save.
	getCertPrimaryJobChangePreview: (departmentId: string, roleString: string, newJobId: string) =>
		axiosInstance.get<CertPrimaryJobChangePreview>(`/departments/${departmentId}/certification-job-mapping-primary-change-preview`, {
			params: { roleString, newJobId },
		}),
	updateDepartmentCertJobMapping: (
		departmentId: string,
		body: { roleString: string; jobIds: string[]; position: string; role: string | null; deactivateNurseIds?: number[]; syncNurseIds?: number[] },
	) => axiosInstance.put<CertificationJobMapping>(`/departments/${departmentId}/certification-job-mapping`, body),
	deleteDepartmentCertJobOverride: (departmentId: string, roleString: string) =>
		axiosInstance.delete<CertMappingDeleteResult>(`/departments/${departmentId}/certification-job-mapping/${encodeURIComponent(roleString)}`),
	getDepartmentEmploymentTypeMapping: (departmentId: string) =>
		axiosInstance.get<EmploymentTypeMapping[]>(`/departments/${departmentId}/employment-type-mapping`),
	updateDepartmentEmploymentTypeMapping: (departmentId: string, body: { rawString: string; employmentType: string }) =>
		axiosInstance.put<EmploymentTypeMapping>(`/departments/${departmentId}/employment-type-mapping`, body),
	deleteDepartmentEmploymentTypeOverride: (departmentId: string, rawString: string) =>
		axiosInstance.delete<EmploymentMappingDeleteResult>(`/departments/${departmentId}/employment-type-mapping/${encodeURIComponent(rawString)}`),
	getDepartmentEmployeeStatusMapping: (departmentId: string) =>
		axiosInstance.get<EmployeeStatusMapping[]>(`/departments/${departmentId}/employee-status-mapping`),
	updateDepartmentEmployeeStatusMapping: (departmentId: string, body: { rawString: string; status: string }) =>
		axiosInstance.put<EmployeeStatusMapping>(`/departments/${departmentId}/employee-status-mapping`, body),
	deleteDepartmentEmployeeStatusOverride: (departmentId: string, rawString: string) =>
		axiosInstance.delete<EmployeeStatusMappingDeleteResult>(`/departments/${departmentId}/employee-status-mapping/${encodeURIComponent(rawString)}`),
	getDepartmentTimeOffTypeMapping: (departmentId: string) => axiosInstance.get<TimeOffTypeMapping[]>(`/departments/${departmentId}/time-off-type-mapping`),
	// `remapPrevious` re-maps the last 60 days of synced time-offs to the new mapping (DAT-200); ignored by the backend when ptoType is null.
	updateDepartmentTimeOffTypeMapping: (
		departmentId: string,
		body: { rawString: string; ptoType: string | null; reason?: string | null; remapPrevious?: boolean },
	) => axiosInstance.put<TimeOffTypeMapping>(`/departments/${departmentId}/time-off-type-mapping`, body),
	deleteDepartmentTimeOffTypeOverride: (departmentId: string, rawString: string) =>
		axiosInstance.delete<TimeOffMappingDeleteResult>(`/departments/${departmentId}/time-off-type-mapping/${encodeURIComponent(rawString)}`),
	getDepartmentJobPpdOverrides: (departmentId: string) => axiosInstance.get<DepartmentJobPpdOverride[]>(`/departments/${departmentId}/job-ppd-overrides`),
	upsertDepartmentJobPpdOverride: (departmentId: string, body: { jobId: string; excludeFromPpd: boolean }) =>
		axiosInstance.put<DepartmentJobPpdOverride>(`/departments/${departmentId}/job-ppd-overrides`, body),
	deleteDepartmentJobPpdOverride: (departmentId: string, jobId: string) =>
		axiosInstance.delete<void>(`/departments/${departmentId}/job-ppd-overrides/${jobId}`),
	listHrisIgnoredValues: (params: { departmentId?: string; includeGlobal?: boolean } = {}) => {
		const query = new URLSearchParams();
		if (params.departmentId) query.set("department_id", params.departmentId);
		if (params.includeGlobal === false) query.set("include_global", "false");
		const qs = query.toString();
		return axiosInstance.get<HrisIgnoredValueRow[]>(`/jobs/hris-ignored-values${qs ? `?${qs}` : ""}`);
	},
	createHrisIgnoredValue: (body: { category: HrisIgnoreCategory; rawValue: string; departmentId?: string | null; source?: string | null }) =>
		axiosInstance.post<HrisIgnoredValueRow>("/jobs/hris-ignored-values", body),
	deleteHrisIgnoredValue: (id: number) => axiosInstance.delete<void>(`/jobs/hris-ignored-values/${id}`),
	createSftpUser: (clientId: string) =>
		axiosInstance.post<{ address: string; port: number; user: string; password: string }>("/sftp/new-sftp", {
			client_id: clientId,
		}),
	createSftpShareLink: () => axiosInstance.post<{ link_token: string }>("/sftp/create-share-link"),
	createSftpUserViaToken: (clientId: string, linkToken: string) =>
		axiosInstance.post<{ address: string; port: number; user: string; password: string }>("/sftp/new-sftp-via-token", {
			client_id: clientId,
			link_token: linkToken,
		}),
	/** Creates the SFTP folder AND the user whose home directory it is — the credentials come back once. */
	createSftpFolder: (folderName: string) =>
		axiosInstance.post<{ address: string; port: number; user: string; password: string }>("/sftp/folders", {
			folder_name: folderName,
		}),
	getSftpFileUrl: (folder: string, file: string) => axiosInstance.get<{ url: string }>("/integrations/sftp/file-url", { params: { folder, file } }),
	getCallInReasons: (departmentId: string) => axiosInstance.get<CallInReason[]>(`/departments/${departmentId}/call-in-reasons`),
	createCallInReason: (departmentId: string, body: { label: string; sortOrder?: number }) =>
		axiosInstance.post<CallInReason>(`/departments/${departmentId}/call-in-reasons`, body),
	updateCallInReason: (reasonId: string, body: { label?: string; sortOrder?: number }) => axiosInstance.put<CallInReason>(`/call-in-reasons/${reasonId}`, body),
	archiveCallInReason: (reasonId: string) => axiosInstance.delete<CallInReason>(`/call-in-reasons/${reasonId}`),
	reorderCallInReasons: (departmentId: string, items: Array<{ reasonId: string; sortOrder: number }>) =>
		axiosInstance.put<CallInReason[]>(`/departments/${departmentId}/call-in-reasons/order`, items),

	getRatingPromptState: async (): Promise<{
		lastReviewPromptAt: DateTime | undefined;
		approvedShiftRequestsCount: number;
		mostRecentApprovalAt: DateTime | undefined;
	}> => {
		const { data } = await axiosInstance.get<{
			lastReviewPromptAt: DateTime | undefined;
			approvedShiftRequestsCount: number;
			mostRecentApprovalAt: DateTime | undefined;
		}>("/users/rating-prompt-state");
		return data;
	},
	markRatingPromptShown: async (): Promise<void> => {
		await axiosInstance.post<void>("/users/rating-prompt-shown");
	},
	getPbjModel: (departmentId: string) =>
		axiosInstance.get<PbjModel>("/pbj-model", {
			params: { department_id: departmentId },
		}),
	upsertPbjModel: (body: PbjModelUpsertBody) => axiosInstance.put<PbjModel>("/pbj-model", body),
	getPbjPrediction: (departmentId: string, startDate: string, endDate: string) =>
		axiosInstance.get<PbjPrediction>("/pbj-model/prediction", {
			params: { department_id: departmentId, start_date: startDate, end_date: endDate },
		}),
	// Re-optimize the "Suggest plan" greedy around manually-added buckets. With an empty
	// `lockedActions` the response equals the prediction's pre-computed suggestedPlan; with
	// locked actions the remaining buckets are optimized as if the locked ones are applied.
	postPbjSuggestedPlan: (body: PbjSuggestedPlanRequest) => axiosInstance.post<PbjSuggestedPlan>("/pbj-model/suggested-plan", body),
	// Commit the assembled Action Plan to the tracker. Storage is append-only / latest-wins, so
	// "switching" plans is another POST rather than an update.
	postPbjTrackedPlan: (body: PbjTrackedPlanSaveBody) => axiosInstance.post<PbjTrackedPlan>("/pbj-model/tracked-plan", body),
	// Progress against the tracked plan, over the PLAN's own quarter. Answers 200 with a NULL body
	// (not a 404) when the department tracks nothing, so the tracker's empty state is never
	// rendered off a failed request.
	getPbjTrackedPlanProgress: (departmentId: string) =>
		axiosInstance.get<PbjPlanProgress | null>("/pbj-model/tracked-plan/progress", {
			params: { department_id: departmentId },
		}),
	// Stop tracking — drops the department's whole plan history. Idempotent (204 either way).
	deletePbjTrackedPlan: (departmentId: string) => axiosInstance.delete<void>("/pbj-model/tracked-plan", { params: { department_id: departmentId } }),
	// DAT-209: pbj_shift-backed Staffing Hours (keyed by pbj_employee_id) — drives the Audit tab table.
	getPbjStaffingHoursPbj: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjStaffingHoursRow[]>("/pbj-model/audit/staffing-hours-pbj", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	// Group-by summary of the Staffing Hours rows (per-job totals, weekend/weekday split, agency hours).
	getPbjStaffingHoursSummary: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjStaffingHoursSummary>("/pbj-model/audit/staffing-hours-pbj/summary", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	// DAT-209 manual editing of a Staffing-Hours cell, persisted on the pbj_shift row.
	patchPbjStaffingShift: (pbjShiftId: string, body: PbjShiftManualEditBody) =>
		axiosInstance.patch<void>(`/pbj-model/audit/staffing-hours-pbj/${pbjShiftId}`, body),
	// DAT-209 bulk edit: apply one `edits` object (absent = untouched, null = clear) to every
	// pbj_shift contributing to the selected grid rows (group keys — aggregated rows included).
	bulkPatchPbjStaffingShifts: (body: PbjStaffingBulkEditBody) =>
		axiosInstance.patch<PbjStaffingBulkEditResult>("/pbj-model/audit/staffing-hours-pbj/bulk", body),
	// Which source-specific Staffing Hours columns to hide (EarnCode / Dist Job Code for Empeon-only depts).
	getPbjStaffingColumnVisibility: (departmentId: string) =>
		axiosInstance.get<PbjStaffingColumnVisibility>("/pbj-model/audit/staffing-hours-pbj/column-visibility", {
			params: { department_id: departmentId },
		}),
	// DAT-209 lazy fetch of the underlying pbj_shift rows for one Staffing-Hours group (row-expand editor).
	getPbjStaffingShifts: (params: { departmentId: string; pbjEmployeeId: string; date: string; pbjJobId: string | null }) =>
		axiosInstance.get<PbjStaffingShiftRow[]>("/pbj-model/audit/staffing-hours-pbj/shifts", {
			params: {
				department_id: params.departmentId,
				pbj_employee_id: params.pbjEmployeeId,
				date: params.date,
				...(params.pbjJobId ? { pbj_job_id: params.pbjJobId } : {}),
			},
		}),
	// File-importable HRIS shift sources for a department (drives the Import dialog's source picker).
	getImportShiftSources: (departmentId: string) =>
		axiosInstance.get<string[]>("/pbj-model/audit/import-shift-sources", { params: { department_id: departmentId } }),
	// Upload an HRIS shifts export and run it through that source's ShiftSyncer (UI equivalent of the cache script).
	importHrisShifts: (params: {
		departmentId: string;
		source: string;
		file: File;
		syncAllInNetwork: boolean;
		dryRun: boolean;
		startDate?: string;
		endDate?: string;
	}) => {
		const formData = new FormData();
		formData.append("file", params.file);
		formData.append("department_id", params.departmentId);
		formData.append("source", params.source);
		formData.append("sync_all_in_network", String(params.syncAllInNetwork));
		formData.append("dry_run", String(params.dryRun));
		if (params.startDate) formData.append("start_date", params.startDate);
		if (params.endDate) formData.append("end_date", params.endDate);
		return axiosInstance.post<PbjShiftImportResult>("/pbj-model/audit/import-shifts", formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
	},
	// Upload a punch-less "manual hours" (admin submission) file. dryRun=true returns the full diff
	// preview (no writes); dryRun=false commits the inserts/updates/soft-deletes. Same file both times.
	importManualHours: (params: { departmentId: string; file: File; dryRun: boolean }) => {
		const formData = new FormData();
		formData.append("file", params.file);
		formData.append("department_id", params.departmentId);
		formData.append("dry_run", String(params.dryRun));
		return axiosInstance.post<PbjManualImportPreview>("/pbj-model/audit/import-manual-hours", formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
	},
	getPbjAuditEmployees: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjAuditEmployeeRow[]>("/pbj-model/audit/employees", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	patchPbjEmployeeOverrideId: (body: PbjEmployeeOverrideIdBody) => axiosInstance.patch<void>("/pbj-model/audit/employees/pbj-override-id", body),
	// Set/clear the manual Pay Type Code override for one roster row (keyed by the RAW synced pbj_id).
	// payTypeCode=null clears the override (reverts to the synced code). 204 on success.
	patchPbjEmployeePayType: (body: PbjPayTypeOverrideBody) => axiosInstance.patch<void>("/pbj-model/audit/employees/pay-type-override", body),
	// Set (exclude=true, non-blank reason MANDATORY) or clear (exclude=false, reason=null) the manual
	// "Exclude employee from PBJ" flag for one employee, keyed by the RAW synced pbj_id. 204 on success.
	patchPbjEmployeeExclude: (body: PbjExcludeEmployeeBody) => axiosInstance.patch<void>("/pbj-model/audit/employees/pbj-exclude", body),
	// Bulk-exclude employees from an ID + Reason list, keyed by the RAW synced pbj_id. A non-blank reason
	// excludes; a blank reason clears an existing exclusion. dryRun=true returns the full diff preview (no
	// writes); dryRun=false commits. A block-severity warning (unknown / duplicate pbj_id) refuses the commit.
	importPbjExclusions: (departmentId: string, file: File, dryRun: boolean) => {
		const formData = new FormData();
		formData.append("file", file);
		formData.append("department_id", departmentId);
		formData.append("dry_run", String(dryRun));
		return axiosInstance.post<PbjExclusionImportPreview>("/pbj-model/audit/employees/pbj-exclude/import", formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
	},
	// One row per employee excluded from PBJ across the facility group, with the dropped hours for the range.
	getPbjExcludedEmployees: (departmentId: string, startDate: string, endDate: string) =>
		axiosInstance.get<PbjExcludedEmployeeRow[]>("/pbj-model/audit/employees/pbj-excluded", {
			params: { department_id: departmentId, start_date: startDate, end_date: endDate },
		}),
	// Upload an overrides file (Override ID / Pay Type Code per employee). dryRun=true returns the full
	// diff preview (no writes); dryRun=false commits the new/updated/removed overrides. Same file both times.
	importPbjOverrides: (params: { departmentId: string; file: File; dryRun: boolean }) => {
		const formData = new FormData();
		formData.append("file", params.file);
		formData.append("department_id", params.departmentId);
		formData.append("dry_run", String(params.dryRun));
		return axiosInstance.post<PbjOverrideImportPreview>("/pbj-model/audit/employees/import-overrides", formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
	},
	getPbjAuditTurnover: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjAuditTurnoverResponse>("/pbj-model/audit/turnover", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	// Audit raw-export sheets — read-only views mirroring the PBJ raw XLSX. Same param shape as audit/employees.
	getPbjRoleSummary: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjRoleSummaryRow[]>("/pbj-model/audit/role-summary-pbj", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	// Total net-of-breaks hours per role per dept-local day for the Audit "Hours by Role" chart,
	// shaped { hoursByRole: { <roleLabel>: { <ISODate>: hours } } }. Same param shape as the sibling sheets.
	getPbjHoursByRoleDaily: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjHoursByRoleDaily>("/pbj-model/audit/hours-by-role-daily", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	// Agency-only staffing hours; same row shape as the (full) staffing-hours-pbj endpoint.
	getPbjAgencyStaffingHours: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjStaffingHoursRow[]>("/pbj-model/audit/agency-staffing-hours-pbj", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	getPbjBreaksRemoved: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjBreaksRemovedRow[]>("/pbj-model/audit/breaks-removed-pbj", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	getPbjTerminated: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjTerminatedEmployeeRow[]>("/pbj-model/audit/terminated-pbj", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	getPbjMultipleRoles: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjMultipleRoleRow[]>("/pbj-model/audit/multiple-roles-pbj", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	getPbjMissingPunches: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjMissingPunchRow[]>("/pbj-model/audit/missing-punches-pbj", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	getPbjLongShifts: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjLongShiftRow[]>("/pbj-model/audit/long-shifts-pbj", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	getPbjLongShiftReview: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjLongShiftReviewResponse>("/pbj-model/audit/long-shift-review-pbj", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	getPbjShortShiftReview: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjShortShiftReviewResponse>("/pbj-model/audit/short-shift-review-pbj", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	// DAT-810: which table the Audit tab's numbers came from (no date range — it's a property of the
	// department's PBJ onboarding, not of the viewed period).
	getPbjAuditWorkedSource: (departmentId: string) =>
		axiosInstance.get<PbjAuditWorkedSource>("/pbj-model/audit/worked-source", {
			params: { department_id: departmentId },
		}),
	getPbjSubmissionHourCapReport: (startDate: string, endDate: string, departmentId: string) =>
		axiosInstance.get<PbjDailyHoursCapReportResponse>("/pbj-model/audit/submission-hour-cap-report", {
			params: { start_date: startDate, end_date: endDate, department_id: departmentId },
		}),
	// Evaluated as-of-today (no date-range params — the audit quarter doesn't apply);
	// thresholdDays is a view-only filter (BE validates ge=1).
	getPbjUpcomingTurnover: (departmentId: string, thresholdDays: number) =>
		axiosInstance.get<PbjUpcomingTurnoverRow[]>("/pbj-model/audit/upcoming-turnover", {
			params: { department_id: departmentId, threshold_days: thresholdDays },
		}),
	downloadPbjAuditRawExport: (departmentId: string, startDate: string, endDate: string) =>
		axiosInstance.get<ArrayBuffer>("/pbj-model/audit/raw-export", {
			params: { department_id: departmentId, start_date: startDate, end_date: endDate },
			responseType: "arraybuffer",
		}),
	// `processType` is the CMS wire token stamped on both submission files: "merge" for a normal
	// filing, "replace" when refiling a quarter CMS already accepted. Always sent explicitly —
	// the user picks it in the download menu, it is never inferred.
	downloadPbjAuditSubmissionExport: (departmentId: string, startDate: string, endDate: string, processType: PbjSubmissionProcessType) =>
		axiosInstance.get<ArrayBuffer>("/pbj-model/audit/submission-export", {
			params: { department_id: departmentId, start_date: startDate, end_date: endDate, process_type: processType },
			responseType: "arraybuffer",
		}),
	// Prediction-tab manual hours: lump-sum extra hours added on top of worked-shift
	// buckets when scoring the Prediction. Distinct from the Audit-tab manual hours
	// above (which are per-nurse-per-weekday). `isRn=true` contributes to BOTH the
	// RN and Total HPPD buckets; weekend HPPD is intentionally not affected.
	getPbjPredictionManualHours: (departmentId: string, startDate: string, endDate: string) =>
		axiosInstance.get<PbjPredictionManualHoursEntry[]>("/pbj-model/prediction/manual-hours", {
			params: { department_id: departmentId, start_date: startDate, end_date: endDate },
		}),
	createPbjPredictionManualHours: (body: PbjPredictionManualHoursCreateBody) =>
		axiosInstance.post<PbjPredictionManualHoursEntry>("/pbj-model/prediction/manual-hours", body),
	updatePbjPredictionManualHours: (entryId: string, body: PbjPredictionManualHoursUpdateBody) =>
		axiosInstance.patch<PbjPredictionManualHoursEntry>(`/pbj-model/prediction/manual-hours/${entryId}`, body),
	deletePbjPredictionManualHours: (entryId: string, departmentId: string) =>
		axiosInstance.delete<void>(`/pbj-model/prediction/manual-hours/${entryId}`, {
			params: { department_id: departmentId },
		}),
	// DAT-608: PBJ-relevant jobs (DON, ADON, MDS Coordinator, …) contributing NO hours to the
	// prediction — neither a punched shift nor a manual-hours entry — over part of the window.
	getPbjPredictionJobCoverage: (departmentId: string, startDate: string, endDate: string) =>
		axiosInstance.get<PbjPredictionJobCoverageGap[]>("/pbj-model/prediction/manual-hours/coverage", {
			params: { department_id: departmentId, start_date: startDate, end_date: endDate },
		}),
	// PBJ Jobs tab (DAT-198): read the role_string → PBJ job mapping + the per-dept job registry,
	// and save the whole changeset (job CRUD + mapping edits) atomically — optionally across the network.
	getPbjJobMapping: (departmentId: string) =>
		axiosInstance.get<PbjJobMappingRow[]>("/pbj-model/jobs/mapping", {
			params: { department_id: departmentId },
		}),
	getPbjJobs: (departmentId: string) =>
		axiosInstance.get<PbjJob[]>("/pbj-model/jobs", {
			params: { department_id: departmentId },
		}),
	savePbjJobs: (body: PbjJobsSaveBody) => axiosInstance.post<PbjJobsSaveResult>("/pbj-model/jobs/save", body),
	// PBJ Breaks tab: read/replace the dept's purpose='pbj' break rules (optionally across the network).
	getPbjBreakRules: (departmentId: string) =>
		axiosInstance.get<PbjBreakRule[]>("/pbj-model/break-rules", {
			params: { department_id: departmentId },
		}),
	savePbjBreakRules: (body: PbjBreakRulesSaveBody) => axiosInstance.post<PbjBreakRulesSaveResult>("/pbj-model/break-rules/save", body),
	getHiringAbsenceSettings: (departmentId: string) =>
		axiosInstance.get<HiringAbsenceSettings>("/hiring/absence-settings", { params: { department_id: departmentId } }),
	updateHiringAbsenceSettings: (departmentId: string, body: HiringAbsenceSettingsUpdate) =>
		axiosInstance.put<HiringAbsenceSettings>("/hiring/absence-settings", body, { params: { department_id: departmentId } }),
	getHiringRecurringGaps: (
		departmentId: string,
		params: {
			census?: number;
			jobId?: string;
			includedMetrics?: string[];
			shiftsPerWeek?: number;
			shiftLength?: number;
			shiftTypes?: string;
			hireShiftLabels?: string[];
		},
	) =>
		axiosInstance.get<HiringRecurringGapsResponse>("/hiring/recurring-gaps", {
			params: {
				department_id: departmentId,
				...(params.census !== undefined ? { census: params.census } : {}),
				...(params.jobId ? { job_id: params.jobId } : {}),
				// Suggested-hire shape. Omitted (not zero) when the user hasn't chosen: the backend then
				// falls back to the selected job's own popular shift length and the FTE week it implies.
				...(params.shiftsPerWeek !== undefined ? { shifts_per_week: params.shiftsPerWeek } : {}),
				...(params.shiftLength !== undefined ? { shift_length: params.shiftLength } : {}),
				// `one` / `many`, never a numeric cap: self-describing in the URL, and it keeps the control's
				// value a string. Omitted until the user picks, so the backend applies its own default.
				...(params.shiftTypes !== undefined ? { hire_shift_types: params.shiftTypes } : {}),
				// Send a CSV string (joined here), not an array: query-string DROPS empty arrays, which would
				// make "all levers off" ([]) indistinguishable from "unspecified" and let the backend fall
				// back to its OT default. An empty string IS preserved (`included_metrics=`), so the backend
				// can tell "explicitly none" from omitted. Sent whenever the caller provides the array.
				...(params.includedMetrics !== undefined ? { included_metrics: params.includedMetrics.join(",") } : {}),
				// Which shift types the Suggested Hires packer may use. CSV for the same reason as the metrics
				// above: an empty array would be dropped from the query string, making "every shift switched
				// off" read as "unfiltered" and packing the whole gap the user just said they weren't hiring for.
				...(params.hireShiftLabels !== undefined ? { hire_shift_labels: params.hireShiftLabels.join(",") } : {}),
			},
			paramsSerializer: (queryParams) => queryString.stringify(queryParams),
		}),
	getHiringEligibleStaff: (departmentId: string, params: { fromDay: number; toDay: number; fromShift: string; jobId: string }) =>
		axiosInstance.get<HiringEligibleStaffResponse>("/hiring/eligible-staff", {
			params: {
				department_id: departmentId,
				from_day: params.fromDay,
				to_day: params.toDay,
				from_shift: params.fromShift,
				job_id: params.jobId,
			},
		}),
	getHiringStaffedNurses: (departmentId: string, params: { dayIdx: number; weekIdx: number; shiftLabel: string; jobId: string }) =>
		axiosInstance.get<HiringStaffedNursesResponse>("/hiring/staffed-nurses", {
			params: {
				department_id: departmentId,
				day_idx: params.dayIdx,
				week_idx: params.weekIdx,
				shift_label: params.shiftLabel,
				job_id: params.jobId,
			},
		}),
	getNurseLocationHistory: (departmentId: string, nurseId: number, days: number) =>
		axiosInstance.get<NurseLocationHistoryResponse>(`/departments/${departmentId}/nurse-location-history`, {
			params: { nurse_id: nurseId, days },
		}),

	// --- Careers (IHH's own applicant tracker, `hiring_ihh`). Request bodies, query params and responses are
	// all snake_case — see `pages/Careers/types.ts`. Every endpoint is gated server-side on the flag (no role check)
	// for the department that owns the job, which must be the caller's unless they are support; writes also refuse
	// a read-only caller. --- //
	fetchCareersJobs: (departmentId: string) => {
		return axiosInstance.get<CareersJobsResponse>("/careers/jobs", { params: { department_id: departmentId } });
	},
	createCareersJob: (departmentId: string, body: CareersJobInput) => {
		return axiosInstance.post<CareersJob>("/careers/jobs", body, { params: { department_id: departmentId } });
	},
	/** Full replace: a step/question `id` keeps that row, `null` creates one, an omitted id deletes it. */
	replaceCareersJob: (jobId: string, body: CareersJobInput) => {
		return axiosInstance.put<CareersJob>(`/careers/jobs/${jobId}`, body);
	},
	setCareersJobStatus: (jobId: string, status: CareersJobStatus) => {
		return axiosInstance.patch<CareersJob>(`/careers/jobs/${jobId}`, { status });
	},
	removeCareersJob: (jobId: string) => {
		return axiosInstance.delete<void>(`/careers/jobs/${jobId}`);
	},
	/** One page, newest first. `jobId` null = every job in the department. */
	fetchCareersApplications: (departmentId: string, jobId: string | null, limit: number, offset: number, signal?: AbortSignal) => {
		return axiosInstance.get<CareersApplicationsResponse>("/careers/applications", {
			params: { department_id: departmentId, ...(jobId === null ? {} : { job_id: jobId }), limit, offset },
			signal,
		});
	},
	/** The profile: the application, the person, the job's steps, answers, notes, events and the person's other applications. */
	fetchCareersApplication: (applicationId: string) => {
		return axiosInstance.get<CareersApplicationDetail>(`/careers/applications/${applicationId}`);
	},
	/**
	 * Add a candidate by hand. 409 `careers.candidate.duplicate` (`details.match`) when a NEW person's email is
	 * already a live candidate; 409 `careers.application.exists` (`details.application_id`) when that person
	 * already has a live application on this job.
	 */
	createCareersApplication: (jobId: string, body: CareersApplicationCreateInput) => {
		return axiosInstance.post<CareersApplicationDetail>(`/careers/jobs/${jobId}/applications`, body);
	},
	/** The live candidate in the department with this email (case and surrounding spaces ignored), or `match: null`. */
	fetchCareersCandidateMatch: (departmentId: string, email: string, signal?: AbortSignal) => {
		return axiosInstance.get<CareersCandidateMatchResponse>("/careers/candidates/match", { params: { department_id: departmentId, email }, signal });
	},
	/** Soft-deletes the application; the backend also soft-deletes the person when it was their last live one. */
	deleteCareersApplication: (applicationId: string) => {
		return axiosInstance.delete<void>(`/careers/applications/${applicationId}`);
	},
	/**
	 * Inline edit of the person's fields and/or this application's source; the body carries ONLY the changed keys.
	 * 409 `careers.candidate.duplicate` when the new email is another candidate's.
	 */
	updateCareersApplicationDetails: (applicationId: string, patch: CareersApplicationDetailsPatch) => {
		return axiosInstance.patch<CareersApplicationDetail>(`/careers/applications/${applicationId}/details`, patch);
	},
	/** Any live application but an ended one - that gives 409 `careers.application.closed`. */
	moveCareersApplicationStep: (applicationId: string, stepId: string) => {
		return axiosInstance.patch<CareersApplicationDetail>(`/careers/applications/${applicationId}/step`, { step_id: stepId });
	},
	/**
	 * End the process where it stands (the step does not move). `note` is required on the wire (null for none) and
	 * required as text when the reason is `other`. 409 `careers.application.hired` on a hire, `.closed` when already ended.
	 */
	endCareersApplication: (applicationId: string, body: CareersApplicationEndInput) => {
		return axiosInstance.post<CareersApplicationDetail>(`/careers/applications/${applicationId}/end`, body);
	},
	/** Clears the end; the candidate is back on the step they ended at. Not ended = 200, nothing changes. */
	reopenCareersApplication: (applicationId: string) => {
		return axiosInstance.post<CareersApplicationDetail>(`/careers/applications/${applicationId}/reopen`);
	},
	/**
	 * Mark whether the rejection note was sent to the candidate. Only a rejection accepts it
	 * (409 `careers.application.rejection_note_rejected_only`). Sending `sent` as it already is changes nothing.
	 */
	setCareersRejectionNoteSent: (applicationId: string, sent: boolean) => {
		return axiosInstance.post<CareersApplicationDetail>(`/careers/applications/${applicationId}/rejection-note-sent`, { sent });
	},
	/**
	 * Move to the job's hired step, which marks them hired. 409 `careers.job.no_hired_step` when the job has
	 * none, `careers.application.ended` when the process has ended.
	 */
	hireCareersApplication: (applicationId: string) => {
		return axiosInstance.post<CareersApplicationDetail>(`/careers/applications/${applicationId}/hire`);
	},
	/** Move back to the step just before the hired step, which clears the hire. */
	unhireCareersApplication: (applicationId: string) => {
		return axiosInstance.post<CareersApplicationDetail>(`/careers/applications/${applicationId}/unhire`);
	},
	/**
	 * Set (an ISO datetime WITH its offset) or clear (`null`) the next interview, with who interviews at it (the
	 * department's interviewers; may be none, and must be none when clearing — else 400
	 * `careers.interview.invalid_interviewer`). Any live application but an ended one (409 `careers.application.closed`).
	 */
	setCareersNextInterview: (applicationId: string, at: string | null, interviewerUserIds: string[]) => {
		return axiosInstance.put<CareersApplicationDetail>(`/careers/applications/${applicationId}/next-interview`, {
			at,
			interviewer_user_ids: interviewerUserIds,
		});
	},
	/**
	 * Remind an interviewer of the next interview that their feedback is missing: a bell notification. 404
	 * `careers.interview.not_interviewer` when they are not on it, 409 `careers.interview.feedback_submitted` once given.
	 */
	remindCareersInterviewer: (applicationId: string, userId: string) => {
		return axiosInstance.post<CareersApplicationDetail>(`/careers/applications/${applicationId}/interviewers/${userId}/remind`);
	},
	/** The department's recruiters and interviewers — the only people a note can @mention — sorted by name. */
	fetchCareersMentionableUsers: (departmentId: string) => {
		return axiosInstance.get<CareersMentionableUsersResponse>("/careers/mentionable-users", { params: { department_id: departmentId } });
	},
	/** 400 `careers.note.invalid_mention` when a mentioned id is not a recruiter or interviewer of the department. */
	addCareersApplicationNote: (applicationId: string, body: CareersNoteInput) => {
		return axiosInstance.post<CareersApplicationDetail>(`/careers/applications/${applicationId}/notes`, body);
	},
	/**
	 * Edit a note: the same body the composer posts (text, mentions). Only its author may (403
	 * `careers.note.not_author`); 404 `careers.note.not_found` when it is gone. Returns the whole profile.
	 */
	editCareersApplicationNote: (applicationId: string, noteId: string, body: CareersNoteInput) => {
		return axiosInstance.patch<CareersApplicationDetail>(`/careers/applications/${applicationId}/notes/${noteId}`, body);
	},
	/** Delete a note, with its mentions' bell notifications and its line in the activity log. Author only (403); 404 when gone. */
	deleteCareersApplicationNote: (applicationId: string, noteId: string) => {
		return axiosInstance.delete<CareersApplicationDetail>(`/careers/applications/${applicationId}/notes/${noteId}`);
	},
	/**
	 * Add feedback. 400 `careers.feedback.invalid_scores` unless every current criterion of the job is scored
	 * once, `careers.feedback.invalid_step` for a step of another job. Returns the whole profile.
	 */
	addCareersApplicationFeedback: (applicationId: string, body: CareersFeedbackInput) => {
		return axiosInstance.post<CareersApplicationDetail>(`/careers/applications/${applicationId}/feedback`, body);
	},
	/** Edit feedback (a full replacement). Only its author may (403 `careers.feedback.not_author`); 404 when gone. */
	editCareersApplicationFeedback: (applicationId: string, feedbackId: string, body: CareersFeedbackInput) => {
		return axiosInstance.patch<CareersApplicationDetail>(`/careers/applications/${applicationId}/feedback/${feedbackId}`, body);
	},
	/** Delete feedback, with its scores, mentions' bell notifications and activity-log line. Author only (403); 404 when gone. */
	deleteCareersApplicationFeedback: (applicationId: string, feedbackId: string) => {
		return axiosInstance.delete<CareersApplicationDetail>(`/careers/applications/${applicationId}/feedback/${feedbackId}`);
	},
	/**
	 * One CV per request. The request only STORES the file and returns it `pending`; a background task parses it
	 * within seconds, and the review list polls until it is `parsed` or `failed`. The model is never called here.
	 */
	uploadCareersCv: (jobId: string, file: File) => {
		const formData = new FormData();
		formData.append("file", file);
		return axiosInstance.post<CareersCvUpload>(`/careers/jobs/${jobId}/cv-uploads`, formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
	},
	/**
	 * A CV for an application that has none, from its profile. Same file rules as the job upload, and it also
	 * only STORES the file (`pending`): the background parse attaches it and fills the candidate's EMPTY fields,
	 * which the profile sees on its next fetch. 409 `careers.application.has_cv` / `careers.application.cv_in_progress`.
	 */
	uploadCareersApplicationCv: (applicationId: string, file: File) => {
		const formData = new FormData();
		formData.append("file", file);
		return axiosInstance.post<CareersCvUpload>(`/careers/applications/${applicationId}/cv`, formData, {
			headers: { "Content-Type": "multipart/form-data" },
		});
	},
	/**
	 * Remove the application's CV for good — its file is deleted, and details already filled in from it stay.
	 * Returns the whole profile (`has_cv: false`, a `cv_removed` event first). 404 `careers.cv.not_found` when it has
	 * no CV attached (also while one added from the profile is still being read); 409 `careers.cv.changed` when the
	 * attached CV is no longer `cvUploadId`, the one the reviewer confirmed (someone replaced it meanwhile). Replace is
	 * this, then `uploadCareersApplicationCv`.
	 */
	removeCareersApplicationCv: (applicationId: string, cvUploadId: string) => {
		return axiosInstance.delete<CareersApplicationDetail>(`/careers/applications/${applicationId}/cv`, { params: { cv_upload_id: cvUploadId } });
	},
	/** Oldest first. */
	fetchCareersCvUploads: (jobId: string, statuses: readonly CareersCvUploadStatus[]) => {
		return axiosInstance.get<CareersCvUploadsResponse>(`/careers/jobs/${jobId}/cv-uploads`, {
			params: { status: statuses },
			// The backend reads a REPEATED `status=parsed&status=failed`; axios would send `status[]=`.
			paramsSerializer: (params) => queryString.stringify(params),
		});
	},
	commitCareersCvUpload: (uploadId: string, body: CareersCommitInput) => {
		return axiosInstance.post<CareersApplicationDetail>(`/careers/cv-uploads/${uploadId}/commit`, body);
	},
	/** From `parsed` or `failed` only (else 409 `careers.cv.not_reparseable`). Comes back `pending`, attempts reset. */
	reparseCareersCvUpload: (uploadId: string) => {
		return axiosInstance.post<CareersCvUpload>(`/careers/cv-uploads/${uploadId}/reparse`);
	},
	/**
	 * A stored CV's original bytes, proxied by the API (never a presigned URL) — an application's, or an upload's
	 * still in review. `arraybuffer` also skips the ISO-date interceptor; an error body arrives as an ArrayBuffer
	 * too — decode it with `hydrateArrayBufferError`. The response names the file `cv.<ext>` only; name a download
	 * from the stored original filename.
	 */
	fetchCareersCvFile: (source: CareersCvSource, signal?: AbortSignal) => {
		return axiosInstance.get<ArrayBuffer>(careersCvFilePath(source), { responseType: "arraybuffer", signal });
	},
	/** A Word or text CV as plain text (`text: null` = unreadable). A PDF is refused with 400 `careers.cv.not_text`. */
	fetchCareersCvText: (source: CareersCvSource, signal?: AbortSignal) => {
		return axiosInstance.get<CareersCvTextResponse>(`${careersCvFilePath(source)}/text`, { signal });
	},
	/** `applicationId`: the profile whose CV this is, or null from the review list — so a stale review card can't discard a CV a profile took over. */
	discardCareersCvUpload: (uploadId: string, applicationId: string | null) => {
		return axiosInstance.post<CareersCvUpload>(`/careers/cv-uploads/${uploadId}/discard`, null, {
			params: applicationId === null ? undefined : { application_id: applicationId },
		});
	},
};
